import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createStackNavigator, CardStyleInterpolators, TransitionSpecs } from '@react-navigation/stack';
import { APP_SHELL_CARD_STYLE } from './stackCardStyle';
import { useSelector } from 'react-redux';
import { store, useAppDispatch } from '../store';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, AppState, Linking, Platform, Modal, Pressable, Alert, ScrollView, type ImageSourcePropType } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import BrandLogo from '../components/BrandLogo';
import * as Notifications from 'expo-notifications';
import { useTheme } from '../hooks/useTheme';
import {
  acceptPendingPartnerInvite,
  restoreSession,
  loginWithDeviceTicket,
  showNotification,
  signOut,
} from '../store/actions/auth';
import { activateDesktopHandoffAccount } from '../store/actions/accounts';
import { fetchNotes } from '../store/actions/notes';
import { fetchFeatureFlags, refreshFeatureFlagsIfStale } from '../store/actions/featureFlags';
import { hydrateBrandingFromHost } from '../store/actions/partners';
import { navigationRef } from './navigationRef';
import {
  getPartnerAccessConversationId,
  getPartnerAccessProjectId,
} from './partnerAccessHandoff';
import usePartnerAccessRootHandoff from './usePartnerAccessRootHandoff';
import { setPendingNotificationNav, type PendingNotificationNav } from './pendingNotificationNav';
import { proactiveGapDestination } from './proactiveGapDestination';
import usePendingNotificationHandoff from './usePendingNotificationHandoff';
import { trackNotificationOpen } from './notificationTelemetry';
import { guestLinkOpenedTarget } from '../utilities/guestLinkPush';
import { apiGet, apiPost, getFreshToken, apiProjectsPost, apiWellnessPost, apiRecordsPost } from '../utilities/apiClient';
import { SET_SETTINGS, SET_NAV_PERSONAS } from '../store/constants';
import { openPlanConversation } from '../store/actions/projects';
import { isConversationId } from '../utilities/emailReplyTurn';
// Eager — LoginScreen is shown immediately for unauthenticated users; LockScreen is always rendered
import LoginScreen from '../screens/LoginScreen';
import { lazyWithRetry } from '../utilities/lazyWithRetry';

// Lazy — defers the entire authenticated screen tree until needed, reducing startup parse time
const MFAScreen = lazyWithRetry(() => import('../screens/MFAScreen'));
const ForcePasswordScreen = lazyWithRetry(() => import('../screens/ForcePasswordScreen'));
const RegisterScreen = lazyWithRetry(() => import('../screens/RegisterScreen'));
const ClaimInviteScreen = lazyWithRetry(() => import('../screens/ClaimInviteScreen'));
const PartnerRegisterScreen = lazyWithRetry(() => import('../screens/partner/PartnerRegisterScreen'));
const PartnerLandingScreen = lazyWithRetry(() => import('../screens/partner/PartnerLandingScreen'));
const AccountsScreen = lazyWithRetry(() => import('../screens/AccountsScreen'));
// AppShell decides between MainTabNavigator (regular customer app),
// PartnerTabNavigator (active partner), and PartnerPendingScreen
// (pending/suspended partner) based on session.account.partner +
// state.partners.partner.status. The "Main" Stack.Screen below routes
// here so the partner-vs-customer swap happens transparently without
// duplicating the rest of the auth/lock/notification logic.
const AppShell = lazyWithRetry(() => import('./AppShell'));
const AdminStack = lazyWithRetry(() => import('./AdminStack'));
const IngestionScreen = lazyWithRetry(() => import('../screens/IngestionScreen'));
const InteractiveSurveyScreen = lazyWithRetry(() => import('../screens/InteractiveSurveyScreen'));
const EmergencyCardScreen = lazyWithRetry(() => import('../screens/EmergencyCardScreen'));
const ConnectAuthorizeScreen = lazyWithRetry(() => import('../screens/ConnectAuthorizeScreen'));
import LockScreen from '../components/LockScreen';
import PasskeySetupSheet from '../components/PasskeySetupSheet';
import ShortLinkWebViewHost from '../components/ShortLinkWebViewHost';
import BiometricSetupModal, { shouldPromptBiometricSetup } from '../components/BiometricSetupModal';
import GlobalComposerHost from '../components/GlobalComposerHost';
import VoiceCallApprovalSheet from '../components/VoiceCallApprovalSheet';
import CampaignMessageSheet from '../components/CampaignMessageSheet';
import { showVoiceApproval } from '../utilities/voiceApprovalStore';
import { showCampaignMessage } from '../utilities/campaignMessageStore';
import { showWellnessCheckIn } from '../utilities/wellnessCheckInStore';
import WellnessCheckInSheet from '../components/WellnessCheckInSheet';
import PinSetupModal from '../components/PinSetupModal';
import AdminPasskeyGate from '../components/AdminPasskeyGate';
import OnboardingScreen, { hasSeenOnboarding, markOnboardingComplete, BIOMETRIC_DEFERRED_KEY, PIN_DEFERRED_KEY, NOTIFICATIONS_PROMPTED_KEY } from '../screens/OnboardingScreen';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import useMaintenanceMode from '../hooks/useMaintenanceMode';
import { useSessionTelemetry } from '../hooks/useSessionTelemetry';
import useWatchCompanion from '../hooks/useWatchCompanion';
import Icon from '../components/Icon';
import { SET_LOCKED } from '../store/constants';
import { isBiometricEnabled } from '../utilities/biometricStore';
import { setPendingRegisterPrefill } from '../utilities/pendingRegisterPrefill';
import { setPendingPartnerInvite } from '../utilities/pendingPartnerInvite';
import { runHealthSync } from '../utilities/healthBackground';
import { registerHealthBackgroundTask, unregisterHealthBackgroundTask } from '../utilities/healthBackgroundTask';
import { registerAppleCalendarTask, unregisterAppleCalendarTask } from '../utilities/appleCalendarSync';
import { registerAppleRemindersTask, unregisterAppleRemindersTask } from '../utilities/appleRemindersSync';
import { registerLocationBackgroundUpdates, unregisterLocationBackgroundUpdates, reportLocationNow } from '../utilities/locationBackgroundTask';
import { localDayKey } from '../utilities/healthDayKey';
import {
  addSiriShortcutListener,
  getInitialSiriShortcut,
  SHORTCUT_TYPES,
  type SiriShortcutEventPayload,
} from '../utilities/siriShortcuts';
import type { RootState } from '../store';
import debug from '../utilities/debug';
import { autoRegisterPushToken } from '../utilities/pushRegistration';
import { hapticSplashFade } from '../utilities/haptics';
import { activateWatchSession, pushWatchContext, sendWatchMessage, getBackgroundRefreshStatus } from '../watch';
import {
  useWatchSync,
  setPendingWatchVaultApproval,
  type WatchVaultApprovalRequest,
  type WatchProcessedNoteRequest,
} from '../hooks/useWatchSync';
import WatchVaultApprovalSheet from '../components/WatchVaultApprovalSheet';
import WatchNoteProcessedSheet from '../components/WatchNoteProcessedSheet';
import AIGlowBorder from '../components/AIGlowBorder';
import { subscribeAIGlowOverlay } from '../utilities/aiGlowOverlay';
import { getElectronBridge, isWeb, isElectron } from '../utilities/platform';
import { extractVaultLinkCode, resolveAndOpenVaultLink } from '../utilities/vaultLink';
import { isOwnAppUrl } from '../utilities/appUrl';
import { handOffConnectLink, isConnectAuthorizeUrl } from '../utilities/aiConnector';
import { isClaimInviteUrl } from '../utilities/pendingCredential';
import FinishSignInGate from '../components/FinishSignInGate';
import {
  extractProjectChatDeepLink,
  extractProjectFilesDeepLink,
  extractProjectSuggestionsDeepLink,
  projectChatNavigationTarget,
} from '../utilities/desktopAppHandoff';
import { usePresenceHeartbeat } from '../utilities/usePresenceHeartbeat';
import { useAttributionCapture } from '../utilities/useAttributionCapture';

// Background-visit grace period before AppState `bg → active` re-locks the app.
// All platforms share the same 10-min grace window so the behaviour matches
// the in-app LockScreen subtitle "Locked after 10 min of inactivity". A brief
// app-switch (jump to Messages to copy a code, open a doc picker, reply to a
// text) used to re-lock the app on every return on iOS/Android, which iPad
// users in particular reported as hostile (ED-2123) — the iPad OS multitasks
// constantly and a sub-second background trip triggered a fresh Face ID
// every time. The OS-level device lock (Face ID / passcode after the iPad's
// own auto-lock) is the lower-bound guarantee that protects against pickup
// access during this window.
const BG_RELOCK_GRACE_MS = 10 * 60 * 1000;

// Display notifications when the app is in the foreground
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

// Android 8+ requires a channel before any notification can be displayed.
// Must be called at module level (before any component mounts) so it is ready
// for the very first notification the device receives.
if (Platform.OS === 'android') {
  Notifications.setNotificationChannelAsync('default', {
    name: 'Default',
    importance: Notifications.AndroidImportance.HIGH,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#63B8FF',
  });

  // High-urgency channel for Emergency SOS alerts.
  // MAX importance + aggressive vibration pattern + bypass DND so the alert
  // is always audible regardless of the device's focus/quiet mode.
  Notifications.setNotificationChannelAsync('emergency_sos', {
    name: 'Emergency SOS',
    importance: Notifications.AndroidImportance.MAX,
    vibrationPattern: [0, 500, 200, 500, 200, 500],
    lightColor: '#FF0000',
    bypassDnd: true,
    lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
  });

  // Health alert channel for HealthKit / Health Connect event-driven pushes
  // (irregular rhythm, high/low heart rate, low SpO2, custom rules) sent by
  // backend/api/projects/controllers/health-alert/index.js with
  // channelId: 'health_alert'. Without this registration, Android 8+ silently
  // drops every health-alert push because the channel is unknown.
  // Importance HIGH (one tier below SOS) + bypassDnd to match the backend's
  // timeSensitive: true flag so the alert reaches the user through Focus modes.
  Notifications.setNotificationChannelAsync('health_alert', {
    name: 'Health Alerts',
    importance: Notifications.AndroidImportance.HIGH,
    vibrationPattern: [0, 400, 200, 400],
    lightColor: '#FF8800',
    bypassDnd: true,
    lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
  });
}

// Register the medication_dose notification category with action buttons.
// iOS shows these as swipe actions / long-press sheet; Android shows them as expanded action buttons.
// Called at module level so it runs once on app start before any notification fires.
Notifications.setNotificationCategoryAsync('medication_dose', [
  {
    identifier: 'mark_taken',
    buttonTitle: 'Mark Taken',
    options: { opensAppToForeground: false },
  },
  {
    identifier: 'snooze',
    buttonTitle: 'Snooze 1 hr',
    options: { opensAppToForeground: false },
  },
  {
    identifier: 'skip',
    buttonTitle: 'Skip',
    options: { opensAppToForeground: false, isDestructive: true },
  },
]).catch(() => {});

// Register the refill_reminder category for prescription refill push notifications.
// "Refill Now" opens the app to the medications screen where the Walgreens button is visible.
// "Dismiss" silently dismisses — the reminder has already fired so no backend call needed.
Notifications.setNotificationCategoryAsync('refill_reminder', [
  {
    identifier: 'refill_now',
    buttonTitle: 'Refill Now',
    options: { opensAppToForeground: true },
  },
  {
    identifier: 'dismiss_refill',
    buttonTitle: 'Dismiss',
    options: { opensAppToForeground: false },
  },
]).catch(() => {});

// Register the question_prompt category for survey check-in push notifications.
// "Answer" opens the app directly to the question screen.
// "Dismiss" processes silently and schedules the next question in the queue.
Notifications.setNotificationCategoryAsync('question_prompt', [
  {
    identifier: 'answer_question',
    buttonTitle: 'Answer',
    options: { opensAppToForeground: true },
  },
  {
    identifier: 'dismiss_question',
    buttonTitle: 'Dismiss',
    options: { opensAppToForeground: false },
  },
]).catch(() => {
  // Category registration is best-effort — failure is non-fatal
});

// Register proactive prompt actions.
Notifications.setNotificationCategoryAsync('proactive_prompt', [
  {
    identifier: 'respond_proactive',
    buttonTitle: 'Respond',
    options: { opensAppToForeground: true },
  },
  {
    identifier: 'dismiss_proactive',
    buttonTitle: 'Dismiss',
    options: { opensAppToForeground: false },
  },
]).catch(() => {});

// Watch vault approval — tapping opens the app to the approval sheet.
// No action buttons: the user must open the app to explicitly approve or deny,
// ensuring they can see the credential details before granting access.
Notifications.setNotificationCategoryAsync('watch_vault_approval', []).catch(() => {});

// Wellness quiet-check nudge. "I'm OK" reports in as a background action WITHOUT opening the
// app (mirrors medication_dose/mark_taken). The push carries category 'wellness_checkin', which
// the backend maps to this categoryId so the button renders (services/push/index.js).
Notifications.setNotificationCategoryAsync('wellness_checkin', [
  {
    identifier: 'im_ok',
    buttonTitle: "I'm OK",
    options: { opensAppToForeground: false },
  },
  {
    identifier: 'pause_wellness_24h',
    buttonTitle: 'Pause 24h',
    options: { opensAppToForeground: false },
  },
]).catch(() => {});

// A caregiver's "Were you at <place> with <name>?" (location follow-up on their
// phone). Answered from the lock screen without opening the app; the backend
// maps that question to this categoryId (services/push/index.js). Yes records
// the visit on the plan; No keeps nothing.
Notifications.setNotificationCategoryAsync('location_visit_confirm', [
  {
    identifier: 'visit_yes',
    buttonTitle: 'Yes',
    options: { opensAppToForeground: false },
  },
  {
    identifier: 'visit_no',
    buttonTitle: 'No',
    options: { opensAppToForeground: false },
  },
]).catch(() => {});

const Stack = createStackNavigator();
const ACTIVE_HEALTH_SYNC_INTERVAL_MS = 15 * 60 * 1000;

function RootScreenFallback() {
  const theme = useTheme();
  return (
    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.background }}>
      <ActivityIndicator size="large" color={theme.colors.primary} />
    </View>
  );
}

// Per-screen Suspense boundary (RN v7 `screenLayout`) — same fix as
// MainTabNavigator. A single <Suspense> around the whole root stack made
// navigating to a not-yet-loaded lazy root screen (Main / Admin / Ingestion /
// InteractiveSurvey / Accounts) suspend the ENTIRE navigator to the fallback
// and drop the in-flight transition, hanging on the spinner until the action
// was repeated (chunk now cached → no suspend).
const renderRootScreenSuspense = ({ children }: { children: React.ReactElement }) => (
  <Suspense fallback={<RootScreenFallback />}>{children}</Suspense>
);

/**
 * Navigate in response to a tapped push notification.
 *
 * Expo push sends `data = payload.config` (the inner config object).
 * Question/assignment prompts include `data.url` (added alongside the
 * existing `data.p / data.q / data.project_id` fields).  We prefer
 * `data.url` when present and fall back to reconstructing the route
 * from the individual fields so older notifications still work.
 */
/**
 * Process a "Dismiss" tap on a question push notification silently.
 * Calls the backend to schedule the next question in the queue,
 * skipping the one the user dismissed.
 */
async function handleQuestionDismiss(data: Record<string, any>): Promise<void> {
  const projectId = data?.project_id;
  const accountId = data?.account_id;
  const cognitoId = data?.cognito_id;
  if (!projectId || !accountId || !cognitoId) return;
  try {
    await apiProjectsPost(`/dismiss-prompt/${projectId}/${accountId}/${cognitoId}`, {});
  } catch {
    // Best-effort — failure is non-fatal
  }
}

/**
 * Process a medication notification action button tap silently (no navigation).
 * Called for mark_taken, snooze, and skip action identifiers.
 */
async function handleMedicationAction(
  actionIdentifier: string,
  data: Record<string, any>,
): Promise<void> {
  const medicationId = data?.medication_id;
  const projectId = data?.project_id;
  const accountId = data?.account_id;
  const cognitoId = data?.cognito_id;
  if (!medicationId || !projectId || !accountId || !cognitoId) return;

  try {
    if (actionIdentifier === 'mark_taken') {
      // Local calendar day, matching MedicationsScreen and useWatchSync — the
      // UTC date (previous behavior) is already tomorrow for a US evening dose.
      const today = localDayKey(Date.now());
      await apiRecordsPost(
        `/medications/mark-taken/${medicationId}/${projectId}/${accountId}/${cognitoId}`,
        { taken: true, date: today },
      );
    } else if (actionIdentifier === 'snooze') {
      await apiRecordsPost(
        `/medications/dismiss-reminder/${medicationId}/${projectId}/${accountId}`,
        { action: 'snooze', snooze_minutes: 60 },
      );
    } else if (actionIdentifier === 'skip') {
      await apiRecordsPost(
        `/medications/dismiss-reminder/${medicationId}/${projectId}/${accountId}`,
        { action: 'skip' },
      );
    }
  } catch {
    // Silently ignore — action is best-effort
  }
}

/**
 * Process the wellness "I'm OK" nudge action silently (no navigation). Posts the recipient's
 * ack, which emits an activity event and cancels any pending escalation ladder server-side.
 */
async function handleWellnessCheckin(data: Record<string, any>): Promise<void> {
  const projectId = data?.project_id;
  const accountId = data?.account_id;
  const cognitoId = data?.cognito_id;
  if (!projectId || !accountId || !cognitoId) return;
  try {
    await apiWellnessPost(`/im-ok/${projectId}/${accountId}/${cognitoId}`, {});
  } catch {
    // best-effort
  }
}

/**
 * Answer a caregiver's visit question. It rides location sync (visit_confirm),
 * which finds the question among the caller's own on this plan; the push
 * carried which place and day it was about.
 */
async function answerVisitQuestion(data: Record<string, any>, answer: 'yes' | 'no'): Promise<void> {
  const projectId = data?.project_id;
  const accountId = data?.account_id;
  const cognitoId = data?.cognito_id;
  if (!projectId || !accountId || !cognitoId || !data?.place_id || !data?.visit_date) return;
  try {
    await apiPost(`/location/sync/${projectId}/${accountId}/${cognitoId}`, {
      visit_confirm: { answer, place_id: data.place_id, visit_date: data.visit_date },
    });
  } catch {
    // Best-effort, like the other notification actions.
  }
}

async function handleWellnessPause(data: Record<string, any>): Promise<void> {
  const projectId = data?.project_id;
  const accountId = data?.account_id;
  const cognitoId = data?.cognito_id;
  if (!projectId || !accountId || !cognitoId) return;
  try {
    await apiWellnessPost(`/snooze-checkins/${projectId}/${accountId}/${cognitoId}`, { hours: 24 });
  } catch {
    // Best-effort background action. The next open settings screen remains available.
  }
}

function handleNotificationResponse(response: Notifications.NotificationResponse) {
  // Record the open BEFORE any branching. Every path out of this function — the
  // medication action buttons, the wellness ack, the dismissals, the overlays and
  // the navigations — is a user interacting with a notification, and expo fires
  // this handler for nothing else. Instrumenting per branch would miss the next
  // branch someone adds. Deduped and PHI-safe in ./notificationTelemetry.
  trackNotificationOpen(response);

  const data: Record<string, any> = (response.notification.request.content.data as any) ?? {};
  const content: Record<string, any> = (response.notification.request.content as any) ?? {};
  const safeDecode = (value: any): string | undefined => {
    if (value == null) return undefined;
    const str = String(value);
    try { return decodeURIComponent(str); } catch { return str; }
  };
  const parseDetailContext = (value: any): Record<string, unknown> | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object') return value as Record<string, unknown>;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
      } catch {}
    }
    return undefined;
  };
  const notificationTitle = safeDecode(content?.title);
  const notificationBody = safeDecode(content?.body);

  // Notification action buttons — process silently where possible.
  const actionId = response.actionIdentifier;

  // Medication actions: Mark Taken / Snooze / Skip — no navigation.
  if (actionId === 'mark_taken' || actionId === 'snooze' || actionId === 'skip') {
    handleMedicationAction(actionId, data).catch(() => {});
    return;
  }

  // Wellness "I'm OK" — silent ack, cancels the escalation ladder, no navigation.
  if (actionId === 'im_ok') {
    handleWellnessCheckin(data).catch(() => {});
    return;
  }

  if (actionId === 'pause_wellness_24h') {
    handleWellnessPause(data).catch(() => {});
    return;
  }

  // A caregiver's visit question, answered from the notification's buttons.
  if (actionId === 'visit_yes' || actionId === 'visit_no') {
    answerVisitQuestion(data, actionId === 'visit_yes' ? 'yes' : 'no').catch(() => {});
    return;
  }

  // Question Dismiss — schedule next question silently, no navigation.
  if (actionId === 'dismiss_question') {
    handleQuestionDismiss(data).catch(() => {});
    return;
  }

  // Proactive prompt dismiss — no backend mutation required.
  if (actionId === 'dismiss_proactive') {
    return;
  }

  // Refill Dismiss — notification already delivered, nothing to do on backend.
  if (actionId === 'dismiss_refill') {
    return;
  }

  // Refill Now / Question Answer — fall through to normal navigation logic below.
  // (opensAppToForeground: true brings the app to front; the URL/category matching handles routing.)

  // The visit question TAPPED (the notice, not a button): ask the same thing
  // here. Native only: an RN-web Alert has no buttons, so web keeps the normal
  // location follow-up routing below.
  if (
    data?.category === 'location_followup' && data?.confirm === true
    && Platform.OS !== 'web'
  ) {
    Alert.alert(notificationTitle || 'Were you there?', notificationBody, [
      { text: 'No', style: 'cancel', onPress: () => { answerVisitQuestion(data, 'no').catch(() => {}); } },
      { text: 'Yes', onPress: () => { answerVisitQuestion(data, 'yes').catch(() => {}); } },
    ]);
    return;
  }

  // Wellness check-in push TAPPED (the body, not the "I'm OK" action button,
  // which acked silently above) — present the full-screen red→green confirm
  // instead of navigating anywhere. Global overlay; needs no navigator.
  const wellnessTapCategory = data?.category as string | undefined;
  if (
    (wellnessTapCategory === 'wellness_check_now' || wellnessTapCategory === 'wellness_checkin')
    && data?.project_id && data?.account_id && data?.cognito_id
  ) {
    showWellnessCheckIn({
      projectId: String(data.project_id),
      accountId: String(data.account_id),
      cognitoId: String(data.cognito_id),
    });
    return;
  }

  // Chat message — open the project's Communicate screen on the Chat
  // tab with the right room pre-selected. Account-scope DMs (no
  // project_id) route to the admin account chat in SettingsStack.
  // Read category off `data` inline — `const category` below this
  // branch is still in the TDZ at this point.
  // Voice call approval (call-in Ellie push-approve) — present the global
  // "Approve call from Ellie?" sheet instead of navigating. Approve/Deny hit the
  // backend to promote or reject the live call. Doesn't require navigation to be
  // ready — the sheet is a global overlay.
  if ((data?.category as string | undefined) === 'voice_call_approval' && data?.call_id) {
    showVoiceApproval({
      callId: String(data.call_id),
      phone: data?.phone ? String(data.phone) : undefined,
      planName: data?.plan_name ? String(data.plan_name) : undefined,
    });
    return;
  }

  // Campaign push — present the step's own content instead of navigating. The
  // push has always carried `url: /campaign/{enrollment}/{step}` and nothing
  // opened it, so the notification led nowhere.
  //
  // Keyed on the enrollment rather than the category alone: a step's
  // push_config can override `category`, and the pair (enrollment_id, step_key)
  // is what only a campaign push carries. An admin who also overrode
  // `deep_link` meant that destination, so those fall through to the generic
  // `data.url` navigation below rather than opening the sheet.
  const campaignUrl = typeof data?.url === 'string' ? (data.url as string) : '';
  const campaignUrlIsLanding = campaignUrl.startsWith('/campaign/');
  if (
    data?.enrollment_id && data?.step_key
    && ((data?.category as string | undefined) === 'campaign' || campaignUrlIsLanding)
    && (!campaignUrl || campaignUrlIsLanding)
  ) {
    showCampaignMessage({
      enrollmentId: String(data.enrollment_id),
      stepKey: String(data.step_key),
      campaignKey: data?.campaign_key ? String(data.campaign_key) : undefined,
      title: notificationTitle,
      body: notificationBody,
    });
    return;
  }

  // Parsed plan destinations wait for the unlocked authenticated navigator.
  // Silent actions and global overlays above retain their existing behavior.
  const session = store.getState().session;
  if (!session.authorized || session.locked?.show || !navigationRef.isReady()
      || !navigationRef.getRootState()?.routes?.some(route => route.name === 'Main')) {
    const pending = parseNotificationResponseForPending(response);
    if (pending) {
      if (Number.isSafeInteger(pending.projectId) && pending.projectId > 0) setPendingNotificationNav(pending);
      return;
    }
  }

  // Access request — open the existing Members form for the requesting member.
  if ((data?.category as string | undefined) === 'permission_request' && data?.project_id) {
    const requestProjectId = Number(data.project_id);
    if (Number.isFinite(requestProjectId)) {
      // `as const` on the tab: without it TS widens 'members' to string, and
      // the pending-nav fallback below stops type-checking against
      // PendingNotificationNav. That mismatch used to be papered over with an
      // `as any` on setPendingNotificationNav, which is how the fallback came
      // to store a screen the consumer had no branch for.
      const params = {
        projectId: requestProjectId,
        tab: 'members' as const,
        openMemberCognitoId: data?.requester_cognito_id ? String(data.requester_cognito_id) : undefined,
      };
      try {
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectContacts', params },
        });
      } catch {
        setPendingNotificationNav({ screen: 'ProjectContacts', ...params });
        (navigationRef as any).navigate('Main', { screen: 'Plans' });
      }
      return;
    }
  }

  const chatPushCategory = data?.category as string | undefined;
  if ((chatPushCategory === 'chat_message' || data?.chat === true) && navigationRef.isReady()) {
    const roomIdRaw = data?.room_id;
    const chatRoomId = roomIdRaw != null ? Number(roomIdRaw) : undefined;
    const projectIdRaw = data?.project_id;
    const projectId = projectIdRaw != null && projectIdRaw !== ''
      ? Number(projectIdRaw)
      : null;
    try {
      if (projectId != null && Number.isFinite(projectId)) {
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: {
            screen: 'ProjectCommunicate',
            params: { projectId, initialTab: 'chat', chatRoomId },
          },
        });
      } else {
        (navigationRef as any).navigate('Main', {
          screen: 'Settings',
          params: {
            screen: 'AdminAccountChat',
            params: { chatRoomId },
          },
        });
      }
    } catch {
      if (projectId != null && Number.isFinite(projectId)) {
        setPendingNotificationNav({
          projectId,
          screen: 'ProjectCommunicate',
          initialTab: 'chat',
          chatRoomId,
        } as any);
        (navigationRef as any).navigate('Main', { screen: 'Plans' });
      }
    }
    return;
  }

  // Primary path: url with prompt=1 flag (question / assignment prompts)
  const url = data?.url as string | undefined;
  const promptMatch = url?.match(/^\/project\/(\d+)\?.*[?&]prompt=1/);
  if (promptMatch && navigationRef.isReady()) {
    const projectId = Number(promptMatch[1]);
    const searchPart = url!.includes('?') ? url!.split('?')[1] : '';
    const params = new URLSearchParams(searchPart);
    const openPageName     = params.get('p') ? decodeURIComponent(params.get('p')!) : undefined;
    const openSurveyName   = params.get('s') ? decodeURIComponent(params.get('s')!) : undefined;
    const openQuestionName = params.get('q') ? decodeURIComponent(params.get('q')!) : undefined;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'ProjectSurveys', params: { projectId, openPageName, openSurveyName, openQuestionName } },
      });
    } catch {
      // Plans stack not yet mounted — same fallback the medication and
      // reminder branches use. The pending intent is byte-identical to what
      // the cold-start twin returns for this branch, so a warm tap that loses
      // the race resolves exactly like a cold one.
      setPendingNotificationNav({
        projectId, screen: 'ProjectSurveys', openPageName, openSurveyName, openQuestionName,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Fallback: question/assignment data fields sent directly in the Expo payload
  const category = data?.category as string | undefined;
  if ((category === 'question' || category === 'assignment') && data?.project_id) {
    if (navigationRef.isReady()) {
      const openPageName     = data.p ? decodeURIComponent(String(data.p)) : undefined;
      const openSurveyName   = data.s ? decodeURIComponent(String(data.s)) : undefined;
      const openQuestionName = data.q ? decodeURIComponent(String(data.q)) : undefined;
      const projectId = Number(data.project_id);
      try {
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectSurveys', params: { projectId, openPageName, openSurveyName, openQuestionName } },
        });
      } catch {
        setPendingNotificationNav({
          projectId, screen: 'ProjectSurveys', openPageName, openSurveyName, openQuestionName,
        });
        (navigationRef as any).navigate('Main', { screen: 'Plans' });
      }
    }
    return;
  }

  // Document request push — open the Files screen on the Documents tab and auto-open
  // the upload sheet with the requested document_type prefilled (read-only on upload
  // because the slug rides along with the upload body and lands on the record itself).
  if ((category === 'document_request' || data?.type === 'document_request') && data?.project_id && navigationRef.isReady()) {
    const projectId = Number(data.project_id);
    const prefillDocumentType = safeDecode(data.document_type);
    const prefillDocumentLabel = safeDecode(data.doc_label);
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectFiles',
          params: {
            projectId,
            initialTab: 'documents',
            openUpload: true,
            prefillDocumentType,
            prefillDocumentLabel,
          },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId,
        screen: 'ProjectFiles',
        initialTab: 'documents',
        openUpload: true,
        prefillDocumentType,
        prefillDocumentLabel,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Care-agent nudges: a finding opens on the care notes screen, a contact
  // nudge on the care team, anything else in the plan chat with the question
  // as context so the user types an answer (proactiveGapDestination).
  if (category === 'proactive_gap' && data?.project_id && navigationRef.isReady()) {
    const destination = proactiveGapDestination(data);
    if (!destination) return;
    const { projectId, screen, careNoteId, tab, proactiveGapContext } = destination;
    const params = screen === 'AgentSuggestions'
      ? { projectId, note: careNoteId }
      : screen === 'ProjectContacts'
        ? { projectId, tab }
        : { projectId, proactiveGapContext };
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen, params },
      });
    } catch {
      setPendingNotificationNav(destination);
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // An AI assistant (ChatGPT, Claude) queued items for the family to review —
  // open the plan's "Needs you" list. Not a care-agent nudge, so it does not go
  // through proactiveGapDestination. Same pending-nav fallback as the medication
  // branch below, for the same warm-start race.
  const connectorReviewProjectId = Number(data?.project_id);
  if (category === 'connector_review'
      && Number.isSafeInteger(connectorReviewProjectId) && connectorReviewProjectId > 0 && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'AgentSuggestions', params: { projectId: connectorReviewProjectId } },
      });
    } catch {
      setPendingNotificationNav({ projectId: connectorReviewProjectId, screen: 'AgentSuggestions' });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // A past-chats import finished (or stopped): open its review, where what it
  // kept is listed. Same pending-nav fallback as the branch above.
  const chatImportProjectId = Number(data?.project_id);
  const chatImportId = Number(data?.import_id);
  if (category === 'chat_import'
      && Number.isSafeInteger(chatImportProjectId) && chatImportProjectId > 0 && navigationRef.isReady()) {
    const importId = Number.isSafeInteger(chatImportId) && chatImportId > 0 ? chatImportId : undefined;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'ChatImport', params: { projectId: chatImportProjectId, importId } },
      });
    } catch {
      setPendingNotificationNav({ projectId: chatImportProjectId, screen: 'ChatImport', importId });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Record-change notices ("James O. updated medication Zithromax"): open the
  // project's Ellie chat, because someone reading this on a lock screen wants to
  // ask what changed and why. It has to be the PROJECT-scoped assistant — the
  // account-level AI tab opens to no particular person.
  //
  // Keyed on the category, like every other branch here. The sender also puts a
  // relative '/ai' in `data.url`, but the only branch that reads `url` is the
  // external-URL one at the bottom and it requires a scheme, so before this the
  // notice routed nowhere at all.
  //
  // Wrapped in try/catch with a pending-nav fallback for the same reason the
  // medication branch below is: on a warm start the Plans stack may not be
  // mounted even though navigationRef.isReady() is true, and the nested
  // navigate throws a 'stale' TypeError. Without the fallback the tap routes
  // nowhere and the user lands on whatever the Plans tab was already showing —
  // for a plan-scoped notice, that is Ellie with no plan, telling them to pick
  // one. ProjectChat is already in the PendingNotificationNav union and
  // ProjectsScreen already drains it, so the recovery path costs nothing new.
  if (category?.endsWith('_change') && data?.record_type && data?.project_id && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectChat',
          params: { projectId: Number(data.project_id) },
        },
      });
    } catch {
      setPendingNotificationNav({ projectId: Number(data.project_id), screen: 'ProjectChat' });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Medication reminder / follow-up / refill: open the medications screen for the project.
  // Wrap in try/catch — on a warm start the Plans stack may not yet be mounted even
  // though navigationRef.isReady() is true, which throws a 'stale' TypeError.
  if ((category === 'medication_reminder' || category === 'refill_reminder') && data?.project_id && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectMedications',
          params: { projectId: Number(data.project_id) },
        },
      });
    } catch {
      // Plans stack not yet mounted — store as pending and let ProjectsScreen handle it.
      setPendingNotificationNav({ projectId: Number(data.project_id), screen: 'ProjectMedications' });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Smart reminder — open SmartRemindersScreen with the reminder detail sheet pre-opened.
  if (category === 'reminder' && data?.project_id && navigationRef.isReady()) {
    const recurrenceRule = (data.recurrence_rule || data?.config?.recurrence_rule || {}) as Record<string, any>;
    const detailSmartKey = (recurrenceRule.smart_key ?? data.smart_key) as string | undefined;
    const detailPageId = (recurrenceRule.page_id ?? data.page_id) as string | undefined;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectSmartReminders',
          params: {
            projectId: Number(data.project_id),
            detailSmartKey,
            detailPageId,
            detailKind: 'smart_reminder',
            detailTitle: notificationTitle,
            detailBody: notificationBody,
          },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId: Number(data.project_id),
        screen: 'ProjectSmartReminders',
        detailSmartKey,
        detailPageId,
        detailKind: 'smart_reminder',
        detailTitle: notificationTitle,
        detailBody: notificationBody,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Health alert — open Health Alerts with the event detail sheet pre-opened.
  // This used to land on Smart Reminders, purely because that screen owned a
  // detail sheet: tapping "Irregular heart rhythm detected" opened a list of
  // reminders about blood-glucose checks and IEP reviews, with the alert
  // floating over it. A health alert belongs with health alerts.
  const healthProjectId = Number(data?.project_id ?? data?.projectId);
  if ((category === 'health_alert' || data?.type === 'health_alert') && Number.isFinite(healthProjectId) && navigationRef.isReady()) {
    const detailContext = parseDetailContext(data?.context);
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectHealthAlerts',
          params: {
            projectId: healthProjectId,
            detailKind: 'health_alert',
            detailTitle: notificationTitle,
            detailBody: notificationBody,
            detailOccurredAt: safeDecode(data?.occurredAt ?? data?.triggeredAt),
            detailAlertType: safeDecode(data?.alertType ?? data?.alert_type),
            detailContext,
          },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId: healthProjectId,
        screen: 'ProjectHealthAlerts',
        detailKind: 'health_alert',
        detailTitle: notificationTitle,
        detailBody: notificationBody,
        detailOccurredAt: safeDecode(data?.occurredAt ?? data?.triggeredAt),
        detailAlertType: safeDecode(data?.alertType ?? data?.alert_type),
        detailContext,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // A health-records connection needs reconnecting — open the plan's Health
  // Data screen, where the connection's own Reconnect button is. Same
  // pending-nav fallback as the medication branch, for the same warm-start race.
  const reconnectProjectId = Number(data?.project_id ?? data?.projectId);
  if ((category === 'health_records_reconnect' || data?.type === 'health_records_reconnect')
      && Number.isSafeInteger(reconnectProjectId) && reconnectProjectId > 0 && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'ProjectFasten', params: { projectId: reconnectProjectId } },
      });
    } catch {
      setPendingNotificationNav({ projectId: reconnectProjectId, screen: 'ProjectFasten' });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Wellness caregiver alert / signals-offline notice — open her Wellness timeline.
  const wellnessProjectId = Number(data?.project_id ?? data?.projectId);
  if ((category === 'wellness_alert' || data?.type === 'wellness_alert'
       || category === 'wellness_signals_offline' || data?.type === 'wellness_signals_offline')
      && Number.isFinite(wellnessProjectId) && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: 'Wellness', params: { projectId: wellnessProjectId } },
      });
    } catch {
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Weekly briefing — open the WeeklyBriefingScreen with the briefing pre-selected
  // in the period picker. The fan-out is owned by controllers/prompt/process when
  // the recurring "Weekly briefing schedule" Smart Reminder fires; it sends data.briefing_id
  // alongside data.project_id so the app deep-links straight to the right window.
  if ((category === 'weekly_briefing' || data?.type === 'weekly_briefing') && data?.project_id && navigationRef.isReady()) {
    const projectId = Number(data.project_id);
    const rawBriefingId = data.briefing_id ?? data.briefingId;
    const briefingIdNum = rawBriefingId == null ? undefined : Number(rawBriefingId);
    const briefingId = Number.isFinite(briefingIdNum) ? briefingIdNum : undefined;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectWeeklyBriefing',
          params: { projectId, briefingId },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId,
        screen: 'ProjectWeeklyBriefing',
        briefingId,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Location follow-up — open Notes so the user can capture a note about the
  // detected visit. Fires shortly after the member is detected to have left a
  // care-relevant location.
  if ((category === 'location_followup' || data?.type === 'location_followup') && data?.project_id && navigationRef.isReady()) {
    const projectId = Number(data.project_id);
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectNotes',
          params: {
            projectId,
          },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId,
        screen: 'ProjectNotes',
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Calendar follow-up — open Notes and auto-open TemplateFillSheet with the
  // suggested template (if provided). Fires when the calendar event ends so the
  // user can capture a post-appointment note.
  if ((category === 'calendar_followup' || data?.type === 'calendar_followup') && data?.project_id && navigationRef.isReady()) {
    const projectId = Number(data.project_id);
    const rawTemplateId = data.template_id ?? data.templateId;
    const openTemplateId = rawTemplateId != null ? Number(rawTemplateId) : undefined;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'ProjectNotes',
          params: {
            projectId,
            openTemplateId,
          },
        },
      });
    } catch {
      setPendingNotificationNav({
        projectId,
        screen: 'ProjectNotes',
        openTemplateId,
      });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // New plan-inbox email — open that plan's inbox, where the message is. The
  // receive Lambda (admin receive-application-email) sends data.kind
  // 'inbound_email' with a camelCase projectId; before this branch the tap only
  // opened the app.
  if (data?.kind === 'inbound_email' && data?.projectId && navigationRef.isReady()) {
    const inboxProjectId = Number(data.projectId);
    // A reply to an email Ellie drafted in a chat was posted into that chat
    // (backend services/email/reply-in-chat) and opens there; other mail opens
    // the plan inbox.
    const replyConversationId = isConversationId(data.conversationId) ? data.conversationId : null;
    if (replyConversationId) store.dispatch(openPlanConversation(replyConversationId, inboxProjectId) as any);
    const inboxScreen = replyConversationId ? 'ProjectChat' : 'ProjectInbox';
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: { screen: inboxScreen, params: { projectId: inboxProjectId } },
      });
    } catch {
      setPendingNotificationNav({ projectId: inboxProjectId, screen: inboxScreen });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  // Emergency SOS — navigate to the project's SOS setup screen so caregivers can
  // immediately see emergency contacts and open the location link.
  if (data?.type === 'emergency_sos' && data?.projectId && navigationRef.isReady()) {
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'EmergencySOSSetup',
          params: {
            projectId:   Number(data.projectId),
            locationUrl: data.locationUrl ?? undefined,
            cardUrl:     data.cardUrl ?? undefined,
            eventId:     data.eventId ?? undefined,
          },
        },
      });
    } catch {
      setPendingNotificationNav({ projectId: Number(data.projectId), screen: undefined });
      (navigationRef as any).navigate('Main', { screen: 'Plans' });
    }
    return;
  }

  if (data?.type === 'emergency_sos_audio' || data?.type === 'share_expiry') {
    const pending = parseNotificationResponseForPending(response);
    if (!pending) return;
    try {
      const rootState = navigationRef.isReady() ? navigationRef.getRootState() : undefined;
      if (!rootState?.routes?.some(route => route.name === 'Main')) {
        setPendingNotificationNav(pending);
        return;
      }
      const { screen, ...params } = pending;
      (navigationRef as any).navigate('Main', { screen: 'Plans', params: { screen, params } });
    } catch {
      setPendingNotificationNav(pending);
    }
    return;
  }

  // Watch vault approval — set the pending request in useWatchSync so it constructs
  // the full approval flow (with approve/deny callbacks) once Redux is ready.
  if (data?.type === 'watch_vault_approval') {
    setPendingWatchVaultApproval({
      credentialId: Number(data.credentialId),
      projectId: String(data.projectId ?? ''),
      label: String(data.label ?? 'Credential'),
      credentialType: String(data.credentialType ?? 'login'),
    });
    return;
  }

  // External URLs only (ticket deep links, etc.)
  if (url && (url.startsWith('http://') || url.startsWith('https://') || url.includes('://'))) {
    Linking.openURL(url).catch(() => {});
  }
}

/**
 * Parses a notification response into navigation params WITHOUT navigating.
 * Used on cold start to store the intent for ProjectsScreen to consume once mounted.
 */
let notificationOpenSequence = 0;

function parseNotificationResponseForPending(
  response: Notifications.NotificationResponse,
): PendingNotificationNav | null {
  const data: Record<string, any> = (response.notification.request.content.data as any) ?? {};
  const content: Record<string, any> = (response.notification.request.content as any) ?? {};
  if (data?.type === 'emergency_sos_audio' || data?.type === 'share_expiry') {
    const rawProjectId = data.type === 'emergency_sos_audio' ? data.projectId : data.project_id;
    const projectId = Number(rawProjectId);
    if (!['string', 'number'].includes(typeof rawProjectId) || !Number.isSafeInteger(projectId) || projectId <= 0) return null;
    if (data.type === 'emergency_sos_audio') {
      // Stored notes use the current authenticated session to load audio.
      // Never launch the URL supplied in a push notification.
      const noteId = Number(data.noteId);
      const hasNoteId = ['string', 'number'].includes(typeof data.noteId) && Number.isSafeInteger(noteId) && noteId > 0;
      return {
        projectId,
        screen: 'ProjectNotes',
        notificationRequestId: ++notificationOpenSequence,
        ...(hasNoteId ? { openNoteId: noteId } : {}),
      };
    }
    return {
      projectId,
      screen: data.target_type === 'snapshot' ? 'ProjectSnapshots'
        : data.target_type === 'credential_share' ? 'ProjectVault'
          // Preserve the existing landing for other share types; Home does
          // not yet provide a generic share-management interface.
          : 'ProjectHome',
    };
  }
  const safeDecode = (value: any): string | undefined => {
    if (value == null) return undefined;
    const str = String(value);
    try { return decodeURIComponent(str); } catch { return str; }
  };
  const parseDetailContext = (value: any): Record<string, unknown> | undefined => {
    if (!value) return undefined;
    if (typeof value === 'object') return value as Record<string, unknown>;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
      } catch {}
    }
    return undefined;
  };
  const url = data?.url as string | undefined;
  if (data?.category === 'permission_request' && data?.project_id) {
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectContacts',
      tab: 'members',
      openMemberCognitoId: data.requester_cognito_id ? String(data.requester_cognito_id) : undefined,
    };
  }
  const promptMatch = url?.match(/^\/project\/(\d+)\?.*[?&]prompt=1/);
  if (promptMatch) {
    const projectId = Number(promptMatch[1]);
    const searchPart = url!.includes('?') ? url!.split('?')[1] : '';
    const params = new URLSearchParams(searchPart);
    return {
      projectId,
      screen: 'ProjectSurveys',
      openPageName:     params.get('p') ? decodeURIComponent(params.get('p')!) : undefined,
      openSurveyName:   params.get('s') ? decodeURIComponent(params.get('s')!) : undefined,
      openQuestionName: params.get('q') ? decodeURIComponent(params.get('q')!) : undefined,
    };
  }
  const category = data?.category as string | undefined;
  if ((category === 'question' || category === 'assignment') && data?.project_id) {
    return {
      projectId:        Number(data.project_id),
      screen: 'ProjectSurveys',
      openPageName:     data.p ? decodeURIComponent(String(data.p)) : undefined,
      openSurveyName:   data.s ? decodeURIComponent(String(data.s)) : undefined,
      openQuestionName: data.q ? decodeURIComponent(String(data.q)) : undefined,
    };
  }
  if ((category === 'document_request' || data?.type === 'document_request') && data?.project_id) {
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectFiles',
      initialTab: 'documents',
      openUpload: true,
      prefillDocumentType: safeDecode(data.document_type),
      prefillDocumentLabel: safeDecode(data.doc_label),
    };
  }
  if (category === 'proactive_gap' && data?.project_id) {
    return proactiveGapDestination(data);
  }
  // Cold-start twin of the connector_review branch above.
  const connectorReviewProjectId = Number(data?.project_id);
  if (category === 'connector_review' && Number.isSafeInteger(connectorReviewProjectId) && connectorReviewProjectId > 0) {
    return { projectId: connectorReviewProjectId, screen: 'AgentSuggestions' };
  }
  // Cold-start twin of the chat_import branch above.
  const chatImportProjectId = Number(data?.project_id);
  const chatImportId = Number(data?.import_id);
  if (category === 'chat_import' && Number.isSafeInteger(chatImportProjectId) && chatImportProjectId > 0) {
    return {
      projectId: chatImportProjectId,
      screen: 'ChatImport',
      importId: Number.isSafeInteger(chatImportId) && chatImportId > 0 ? chatImportId : undefined,
    };
  }
  // Cold-start twin of the record-change branch above.
  if (category?.endsWith('_change') && data?.record_type && data?.project_id) {
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectChat',
    };
  }
  if ((category === 'medication_reminder' || category === 'refill_reminder') && data?.project_id) {
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectMedications',
    };
  }
  if (category === 'reminder' && data?.project_id) {
    const recurrenceRule = (data.recurrence_rule || data?.config?.recurrence_rule || {}) as Record<string, any>;
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectSmartReminders',
      detailSmartKey: (recurrenceRule.smart_key ?? data.smart_key) as string | undefined,
      detailPageId: (recurrenceRule.page_id ?? data.page_id) as string | undefined,
      detailKind: 'smart_reminder',
      detailTitle: safeDecode(content?.title),
      detailBody: safeDecode(content?.body),
    };
  }
  if ((category === 'health_alert' || data?.type === 'health_alert') && (data?.project_id || data?.projectId)) {
    const detailContext = parseDetailContext(data?.context);
    return {
      projectId: Number(data.project_id ?? data.projectId),
      screen: 'ProjectHealthAlerts',
      detailKind: 'health_alert',
      detailTitle: safeDecode(content?.title),
      detailBody: safeDecode(content?.body),
      detailOccurredAt: safeDecode(data?.occurredAt ?? data?.triggeredAt),
      detailAlertType: safeDecode(data?.alertType ?? data?.alert_type),
      detailContext,
    };
  }
  // Cold-start twin of the health_records_reconnect branch above.
  if ((category === 'health_records_reconnect' || data?.type === 'health_records_reconnect')
      && (data?.project_id || data?.projectId)) {
    return {
      projectId: Number(data.project_id ?? data.projectId),
      screen: 'ProjectFasten',
    };
  }
  if ((category === 'weekly_briefing' || data?.type === 'weekly_briefing') && data?.project_id) {
    const rawBriefingId = data.briefing_id ?? data.briefingId;
    const briefingIdNum = rawBriefingId == null ? undefined : Number(rawBriefingId);
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectWeeklyBriefing',
      briefingId: Number.isFinite(briefingIdNum) ? briefingIdNum : undefined,
    };
  }
  if ((category === 'location_followup' || data?.type === 'location_followup') && data?.project_id) {
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectNotes',
    };
  }
  if ((category === 'calendar_followup' || data?.type === 'calendar_followup') && data?.project_id) {
    const rawTemplateId = data.template_id ?? data.templateId;
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectNotes',
      openTemplateId: rawTemplateId != null ? Number(rawTemplateId) : undefined,
    };
  }
  // Cold-start twin of the inbound_email branch above.
  if (data?.kind === 'inbound_email' && data?.projectId) {
    return isConversationId(data.conversationId)
      ? { projectId: Number(data.projectId), screen: 'ProjectChat', conversationId: data.conversationId }
      : { projectId: Number(data.projectId), screen: 'ProjectInbox' };
  }
  // Chat message: cold-start variant of the warm-path routing above.
  // ProjectsScreen consumes ProjectCommunicate intents; account-scope
  // pushes (no project_id) drop through to null since they live in
  // SettingsStack and the pending-nav system is plans-scoped.
  if ((category === 'chat_message' || data?.chat === true) && data?.project_id) {
    const roomIdRaw = data?.room_id;
    return {
      projectId: Number(data.project_id),
      screen: 'ProjectCommunicate',
      initialTab: 'chat',
      chatRoomId: roomIdRaw != null ? Number(roomIdRaw) : undefined,
    } as any;
  }

  // Emergency SOS cold-start: handled directly in handleNotificationResponse; return null
  // here so the pending nav system doesn't fight it.
  return null;
}

const maintenanceLogo = require('../../assets/horizontal_logo.png') as ImageSourcePropType;

function MaintenanceOverlay() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const { maintenanceMode, maintenanceTitle, maintenanceMessage } = useMaintenanceMode();
  const [checking, setChecking] = useState(false);
  const styles = useMemo(() => makeMaintenanceStyles(theme), [theme]);

  const handleCheckAgain = useCallback(async () => {
    setChecking(true);
    try {
      const res = await apiGet('/users/get-settings');
      if (res?.payload) {
        dispatch({ type: SET_SETTINGS, payload: res.payload });
      }
    } finally {
      setChecking(false);
    }
  }, [dispatch]);

  const handleLogOut = useCallback(() => {
    dispatch(signOut() as any);
  }, [dispatch]);

  if (!maintenanceMode) return null;

  return (
    <View style={[StyleSheet.absoluteFill, styles.root]}>
      <SafeAreaView style={styles.safe}>
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.card}>
            {/* Brand — reassures the user this is still Eleplan (and shows a
                partner's brand when on a white-label subdomain). */}
            <BrandLogo fallback={maintenanceLogo} style={styles.logo} resizeMode="contain" />

            <View style={styles.iconBadge}>
              <Icon icon={['fad', 'screwdriver-wrench']} size={28} color={theme.colors.primary} />
            </View>

            <Text style={styles.title}>
              {maintenanceTitle || 'Scheduled maintenance'}
            </Text>
            <Text style={styles.message}>
              {maintenanceMessage ||
                "We're making improvements to Eleplan and will be back shortly. Thank you for your patience."}
            </Text>

            <View style={styles.divider} />

            <TouchableOpacity
              onPress={handleCheckAgain}
              disabled={checking}
              activeOpacity={0.85}
              accessibilityRole="button"
              style={[styles.primaryBtn, checking && styles.primaryBtnBusy]}
            >
              {checking
                ? <ActivityIndicator size="small" color={theme.colors.primaryText} />
                : <Text style={styles.primaryBtnText}>Check again</Text>
              }
            </TouchableOpacity>

            {authorized && (
              <TouchableOpacity
                onPress={handleLogOut}
                activeOpacity={0.7}
                accessibilityRole="button"
                style={styles.secondaryBtn}
              >
                <Text style={styles.secondaryBtnText}>Log out</Text>
              </TouchableOpacity>
            )}
          </View>

          <Text style={styles.footnote}>We appreciate your patience.</Text>
        </ScrollView>
      </SafeAreaView>
    </View>
  );
}

const makeMaintenanceStyles = (theme: any) => StyleSheet.create({
  // Match the card surface so the screen reads as one clean white field in
  // light mode (and the theme background in dark) — not a grey wash behind the
  // card. The card's border + shadow are what delineate it.
  root: { backgroundColor: theme.colors.background, zIndex: 9999 },
  safe: { flex: 1 },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 460,
    backgroundColor: theme.colors.card,
    borderRadius: theme.radiusPx.px20,
    borderWidth: 1,
    borderColor: theme.colors.border,
    paddingTop: 40,
    paddingBottom: 36,
    paddingHorizontal: 32,
    alignItems: 'center',
    ...theme.shadow.medium,
  },
  logo: { width: 130, height: 32, marginBottom: 28 },
  iconBadge: {
    width: 72,
    height: 72,
    borderRadius: theme.radius.full,
    backgroundColor: `${theme.colors.primary}14`,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 22,
  },
  title: {
    fontSize: theme.font.size.xxl,
    fontWeight: '700',
    letterSpacing: -0.3,
    color: theme.colors.text,
    textAlign: 'center',
  },
  message: {
    fontSize: theme.font.size.md,
    color: theme.colors.muted,
    textAlign: 'center',
    lineHeight: 23,
    marginTop: 10,
  },
  divider: {
    height: 1,
    alignSelf: 'stretch',
    backgroundColor: theme.colors.border,
    marginTop: 28,
    marginBottom: 24,
  },
  primaryBtn: {
    alignSelf: 'stretch',
    height: 50,
    borderRadius: theme.radius.md,
    backgroundColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnBusy: { opacity: 0.7 },
  primaryBtnText: {
    fontSize: theme.font.size.md,
    fontWeight: '600',
    color: theme.colors.primaryText,
  },
  secondaryBtn: {
    alignSelf: 'stretch',
    height: 46,
    borderRadius: theme.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  secondaryBtnText: {
    fontSize: theme.font.size.md,
    fontWeight: '500',
    color: theme.colors.muted,
  },
  footnote: {
    fontSize: theme.font.size.sm,
    color: theme.colors.muted,
    textAlign: 'center',
    marginTop: 24,
    opacity: 0.85,
  },
});

function OnboardingReplayScreen({ navigation }: { navigation: any }) {
  return (
    <OnboardingScreen
      mode="replay"
      onDone={async () => {
        navigation.goBack();
      }}
    />
  );
}

function handleSiriShortcut(event: SiriShortcutEventPayload) {
  const { activityType, userInfo = {} } = event;
  const projectId = userInfo.projectId ? Number(userInfo.projectId) : null;
  debug.log('ui', 'Siri shortcut received', {
    activityType,
    projectId,
    userInfo,
    navReady: navigationRef.isReady(),
  });

  switch (activityType) {
    case SHORTCUT_TYPES.OPEN_CHAT:
      debug.log('ui', 'Siri navigating to account chat');
      (navigationRef as any).navigate('Main', { screen: 'AI' });
      break;

    case SHORTCUT_TYPES.OPEN_PROJECT_CHAT:
      if (projectId) {
        debug.log('ui', 'Siri navigating to project chat', { projectId });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectChat', params: { projectId } },
        });
      } else {
        debug.warn('Siri project chat shortcut missing projectId');
        (navigationRef as any).navigate('Main', { screen: 'AI' });
      }
      break;

    case SHORTCUT_TYPES.NEW_NOTE:
    case SHORTCUT_TYPES.NEW_VOICE_NOTE:
      if (projectId) {
        debug.log('ui', 'Siri navigating to project notes', {
          projectId,
          shortcutAction: activityType === SHORTCUT_TYPES.NEW_VOICE_NOTE ? 'voice' : 'text',
        });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: {
            screen: 'ProjectNotes',
            params: {
              projectId,
              shortcutAction: activityType === SHORTCUT_TYPES.NEW_VOICE_NOTE ? 'voice' : 'text',
            },
          },
        });
      }
      break;

    case SHORTCUT_TYPES.NEW_MEDICATION:
      if (projectId) {
        debug.log('ui', 'Siri navigating to medications', { projectId });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectMedications', params: { projectId } },
        });
      }
      break;

    case SHORTCUT_TYPES.OPEN_BENEFITS:
      if (projectId) {
        debug.log('ui', 'Siri navigating to benefits', { projectId });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectBenefits', params: { projectId } },
        });
      }
      break;

    case SHORTCUT_TYPES.OPEN_CALENDAR:
      if (projectId) {
        debug.log('ui', 'Siri navigating to calendar', { projectId });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectCalendar', params: { projectId } },
        });
      }
      break;

    case SHORTCUT_TYPES.OPEN_CONTACTS:
      if (projectId) {
        debug.log('ui', 'Siri navigating to contacts', { projectId });
        (navigationRef as any).navigate('Main', {
          screen: 'Plans',
          params: { screen: 'ProjectContacts', params: { projectId } },
        });
      }
      break;

    default:
      break;
  }
}

/** 200ms per try — see the drain re-poke budgets inside RootNavigator. */
const DRAIN_MAX_TRIES = 50;

export default function RootNavigator() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  // Live presence: heartbeat while foregrounded + signed in (for the admin Live tab).
  usePresenceHeartbeat();
  // First-touch acquisition: capture referrer/UTM on load, send once authenticated.
  useAttributionCapture();
  const authorized = useSelector((state: RootState) => state.session.authorized);
  const locked = useSelector((state: RootState) => state.session.locked);
  const oauthInProgress = useSelector((state: RootState) => state.session.oauthInProgress);
  // Post-registration passkey offer. Read here only so the notification sheet
  // below can wait its turn — PasskeySetupSheet renders itself from the same slice.
  const passkeyPrompt = useSelector((state: RootState) => state.session.passkeyPrompt);
  const user = useSelector((state: RootState) => state.authentication.user);
  const account = useSelector((state: RootState) => state.session.account);
  const accountsCurrent = useSelector((state: RootState) => state.accounts.current);
  const unreadChats = useSelector((state: RootState) => state.session.unread_chats);
  const [bootstrapping, setBootstrapping] = useState(true);
  usePendingNotificationHandoff(authorized, !!locked?.show, bootstrapping);
  // Onboarding: shown once on first install, pre-login
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Post-login biometric setup: shown once after first sign-in
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  // Post-login PIN setup: shown if user opted in during onboarding
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showNotificationsPrompt, setShowNotificationsPrompt] = useState(false);
  // True once the post-login security decision (biometric / PIN) has been made
  // for this sign-in. The push-permission sheet must not open before that; see
  // the note on its effect below.
  const [securityPromptsDecided, setSecurityPromptsDecided] = useState(false);
  // The push-permission sheet's own presentation flag. Once true it stays true
  // until the sheet's own onDone; nothing else may flip it.
  const [notificationsSheetOpen, setNotificationsSheetOpen] = useState(false);
  // Controls which screen the navigator lands on after onboarding completes
  const [onboardingTarget, setOnboardingTarget] = useState<'login' | 'register' | undefined>('login');
  const appState = useRef(AppState.currentState);
  const pendingSiriShortcutRef = useRef<SiriShortcutEventPayload | null>(null);
  const initRanRef = useRef(false);
  const handledInitialNotificationRef = useRef<string | null>(null);
  const handledHealthSyncRequestsRef = useRef<Map<string, number>>(new Map());
  const handledWellnessCheckRequestsRef = useRef<Map<string, number>>(new Map());
  const justUnlockedAt = useRef<number>(0);
  const [pendingSiriNonce, setPendingSiriNonce] = useState(0);
  usePartnerAccessRootHandoff(
    authorized && !bootstrapping,
    getPartnerAccessProjectId(accountsCurrent),
    getPartnerAccessConversationId(accountsCurrent),
  );
  const queueSiriShortcut = useCallback((event: SiriShortcutEventPayload) => {
    debug.log('ui', 'Queueing Siri shortcut for later handling', {
      activityType: event?.activityType,
      hasUserInfo: !!event?.userInfo,
      bootstrapping,
      authorized,
      navReady: navigationRef.isReady(),
    });
    pendingSiriShortcutRef.current = event;
    setPendingSiriNonce((n) => n + 1);
  }, [authorized, bootstrapping]);

  const handleRemoteHealthSyncRequest = useCallback(async (data: Record<string, any>) => {
    const projectId = data?.project_id;
    const requestId = data?.request_id;
    const category = data?.category;
    const targetCognitoId = data?.target_cognito_id;
    if (category !== 'health_sync_request' || !projectId || !requestId) return false;
    if (!authorized || !account?.account_id || !user?.cognito_id) return false;
    if (targetCognitoId && String(targetCognitoId) !== String(user.cognito_id)) return false;
    const now = Date.now();
    handledHealthSyncRequestsRef.current.forEach((ts, key) => {
      if (now - ts > 24 * 60 * 60 * 1000) handledHealthSyncRequestsRef.current.delete(key);
    });
    if (handledHealthSyncRequestsRef.current.has(String(requestId))) return true;
    handledHealthSyncRequestsRef.current.set(String(requestId), now);

    debug.log('ui', 'Handling remote health sync request', {
      projectId,
      requestId,
      appState: appState.current,
    });

    const reportBasePath = `/health/report-sync/${projectId}/${account.account_id}/${user.cognito_id}`;

    try {
      await apiWellnessPost(reportBasePath, {
        request_id: requestId,
        status: 'started',
      });

      const outcome = await runHealthSync(true, projectId);

      await apiWellnessPost(reportBasePath, {
        request_id: requestId,
        status: 'completed',
        // What actually moved. `completed` on its own only means the handler
        // ran to the end — a denied HealthKit permission, an ownership skip, a
        // throttle, or (before #2280) a call to a method that does not exist
        // all reached this line looking exactly like a real sync, and the
        // caregiver who tapped "ask them to sync" was told it worked.
        synced: outcome.uploaded,
        reason: outcome.reason,
      });

      debug.log('ui', 'Remote health sync request completed', {
        projectId,
        requestId,
        synced: outcome.uploaded,
        reason: outcome.reason,
      });
    } catch (err: any) {
      await apiWellnessPost(reportBasePath, {
        request_id: requestId,
        status: 'failed',
        error: err?.message || 'Remote health sync failed',
      }).catch(() => {});
      debug.error('Remote health sync request failed', err);
    }

    return true;
  }, [authorized, account?.account_id, user?.cognito_id]);

  // Wellness "check now": a caregiver asked the server to pull fresh signals from THIS
  // (the recipient's) device. Same silent-push remote-pull shape as health sync, with its own
  // category, report endpoint, and request-id dedupe. The forced health sync produces the
  // normal wellness signal events; presence/location refresh naturally on the app wake.
  const handleWellnessCheckNowRequest = useCallback(async (data: Record<string, any>) => {
    const projectId = data?.project_id;
    const requestId = data?.request_id;
    const category = data?.category;
    const targetCognitoId = data?.target_cognito_id;
    if (category !== 'wellness_check_now' || !projectId || !requestId) return false;
    if (!authorized || !account?.account_id || !user?.cognito_id) return false;
    if (targetCognitoId && String(targetCognitoId) !== String(user.cognito_id)) return false;
    const now = Date.now();
    handledWellnessCheckRequestsRef.current.forEach((ts, key) => {
      if (now - ts > 24 * 60 * 60 * 1000) handledWellnessCheckRequestsRef.current.delete(key);
    });
    if (handledWellnessCheckRequestsRef.current.has(String(requestId))) return true;
    handledWellnessCheckRequestsRef.current.set(String(requestId), now);

    const reportBasePath = `/report-check/${projectId}/${account.account_id}/${user.cognito_id}`;
    try {
      await apiWellnessPost(reportBasePath, { request_id: requestId, status: 'started' });
      // Health and location are reported together, because this push is the
      // only moment we know the recipient's phone is awake and listening.
      //
      // Location previously rode ONLY on locationBackgroundTask, which reports
      // on 500m of movement or a 15-minute floor — right for a passive signal,
      // useless to a caregiver who just tapped "Check now" on a phone that has
      // been on a table. Settled in parallel and independently: a denied
      // location permission must not cost the caller their health sync.
      const [, locationReported] = await Promise.all([
        runHealthSync(true, projectId),
        reportLocationNow(projectId),
      ]);
      await apiWellnessPost(reportBasePath, {
        request_id: requestId,
        status: 'completed',
        // What actually got reported. This used to be an unconditional
        // 'completed' with no detail, so a check that reached the phone and
        // collected nothing — a denied permission, or the dead HealthKit call
        // fixed in #2280 — was indistinguishable from one that worked. The
        // caregiver was told it succeeded either way.
        location_reported: locationReported,
      });
    } catch (err: any) {
      await apiWellnessPost(reportBasePath, {
        request_id: requestId,
        status: 'failed',
        error: err?.message || 'Wellness check failed',
      }).catch(() => {});
      debug.error('Wellness check-now request failed', err);
    }
    return true;
  }, [authorized, account?.account_id, user?.cognito_id]);

  // Caregiver-message push handler. Both the "to_recipient" (caregiver →
  // recipient's watch) and "reply_to_sender" (recipient's reply → original
  // caregiver) flows arrive as data-only pushes with type `caregiver_message`
  // or `caregiver_reply`. We forward the payload to the paired watch via
  // WCSession so the existing watch-side CaregiverMessageView can render it,
  // and refresh notes so the conversation history updates.
  //
  // Returns true when the notification was a caregiver message we handled,
  // false otherwise (so the caller can fall through to other handlers).
  const handleCaregiverMessagePush = useCallback(async (data: Record<string, any>) => {
    const type = data?.type;
    if (type !== 'caregiver_message' && type !== 'caregiver_reply') return false;

    const projectId = data?.projectId;
    const messageId = data?.messageId;
    const audioUrl = data?.audioUrl;
    if (!projectId || !messageId || !audioUrl) {
      debug.warn('caregiver_message push missing required fields', { type, projectId, messageId });
      return false;
    }

    if (type === 'caregiver_message') {
      try {
        await sendWatchMessage({
          type: 'caregiver_message',
          messageId: String(messageId),
          projectId: String(projectId),
          senderName: data?.senderName ?? 'Caregiver',
          senderCognitoId: data?.senderCognitoId ?? undefined,
          audioUrl: String(audioUrl),
          sentAt: new Date().toISOString(),
        });
      } catch (err: any) {
        debug.error('Failed to forward caregiver_message to watch', err);
      }
    }

    if (account?.account_id && user?.cognito_id) {
      dispatch(fetchNotes(String(projectId)) as any).catch(() => {});
    }

    return true;
  }, [dispatch, account?.account_id, user?.cognito_id]);

  // Session telemetry (ED-2570): flag-gated journey recorder lifecycle —
  // starts/stops with session_telemetry_enabled + an authenticated session.
  useSessionTelemetry();

  // Global feature flag fetch — kick off whenever a session exists. Server
  // is multi-tier cached (10s L1 / 5min DynamoDB), so re-firing on every
  // session change is cheap and keeps mobile in sync with admin toggles
  // shortly after a flip.
  useEffect(() => {
    if (!account?.account_id || !user?.cognito_id) return;
    dispatch(fetchFeatureFlags() as any).catch(() => {});
  }, [dispatch, account?.account_id, user?.cognito_id]);

  // The session fetch above never re-runs while the app stays signed in, so
  // an admin flip (a kill switch included) would wait for a re-login or cold
  // start. Re-check on foreground once the snapshot is stale.
  useEffect(() => {
    if (!account?.account_id || !user?.cognito_id) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') dispatch(refreshFeatureFlagsIfStale() as any).catch(() => {});
    });
    return () => sub.remove();
  }, [dispatch, account?.account_id, user?.cognito_id]);

  // True only when the app reached the 'background' state (home button / app switch).
  // Native OS overlays (contacts picker, photo picker, camera, etc.) only go through
  // 'inactive' without ever reaching 'background', so we must not lock for those.
  const wentToBackground = useRef(false);
  // Wall-clock timestamp of the last bg transition. Paired with BG_RELOCK_GRACE_MS
  // so the desktop/web flow can distinguish a quick refocus (don't re-lock) from
  // a true idle absence (do re-lock). 0 means we haven't backgrounded yet this
  // session.
  const backgroundedAt = useRef<number>(0);
  // Pending emergency-card navigation — set on cold-launch / warm-reopen of a
  // /emergency/:token URL. Drained by an effect once nav is ready + the app
  // isn't bootstrapping/locked, so the EmergencyCardScreen renders directly
  // (no in-app WebView overlay).
  const [pendingEmergencyCard, setPendingEmergencyCard] = useState<{ token: string; pass_url?: string } | null>(null);
  // Voice-handoff deep link (QR scanned from the desktop app) → open EllieVoice
  // on this phone for the same project/conversation. Drained after auth below.
  const [pendingVoiceHandoff, setPendingVoiceHandoff] = useState<{ projectId: number; conversationId?: string } | null>(null);
  // Re-poke budgets for the two post-auth drains below. They wait for `Main` to
  // appear in the navigator (see authedShellMounted), and `Main` is not
  // guaranteed to EVER appear — a user who stops on account selection never
  // mounts it. Without a cap that wait re-renders the root every 200ms for the
  // rest of the session. 50 × 200ms ≈ 10s, comfortably past the ~1s the shell
  // actually takes, after which the link is dropped rather than spun on.
  const voiceDrainTriesRef = useRef(0);
  const vaultDrainTriesRef = useRef(0);
  const projectChatDrainTriesRef = useRef(0);
  const desktopAccountActivationRef = useRef(false);
  const verifiedDesktopChatRef = useRef<string | null>(null);
  const latestProjectChatKeyRef = useRef<string | null>(null);
  // The voice drain re-pokes with `{ ...p }`, which is a NEW object identity, so
  // the effect re-runs. pendingVaultCode is a STRING — `${c}` produces the same
  // primitive, React bails out of the update, and the effect never fires again.
  // A counter is the only thing that reliably changes for a primitive.
  const [vaultDrainNonce, setVaultDrainNonce] = useState(0);
  // Project-chat link from either a conversation invite or the browser's
  // desktop-app handoff. A missing conversationId means the fresh ProjectChat
  // screen; a present id opens that exact persisted Conversation.
  const [pendingProjectChat, setPendingProjectChat] = useState<{
    projectId: number;
    accountId?: string;
    conversationId?: string;
  } | null>(null);
  const queueProjectChat = useCallback((target: {
    projectId: number;
    accountId?: string;
    conversationId?: string;
  }) => {
    const key = `${target.accountId || ''}:${target.projectId}:${target.conversationId || ''}`;
    latestProjectChatKeyRef.current = key;
    verifiedDesktopChatRef.current = null;
    setPendingProjectChat(target);
  }, []);
  const [shortLinkDeepLink, setShortLinkDeepLink] = useState<string | null>(null);
  // Cold-launch vault link: stash the code until authed + navigator ready, then
  // resolve + open. Warm taps (app already open) resolve immediately in handleUrl.
  const [pendingVaultCode, setPendingVaultCode] = useState<string | null>(null);
  const watchCompanion = useWatchCompanion();
  const [watchVaultApproval, setWatchVaultApproval] = useState<WatchVaultApprovalRequest | null>(null);
  const [watchProcessedNote, setWatchProcessedNote] = useState<WatchProcessedNoteRequest | null>(null);
  const [aiGlowActive, setAIGlowActive] = useState(false);
  const handleWatchVaultApproval = useCallback((req: WatchVaultApprovalRequest) => {
    setWatchVaultApproval(req);
  }, []);
  const handleWatchNoteProcessed = useCallback((req: WatchProcessedNoteRequest) => {
    // Last-write-wins — if a second watch note lands while the first sheet
    // is still open, swap to the newer one. Users rarely machine-gun voice
    // notes, so a queue would be overkill.
    setWatchProcessedNote(req);
  }, []);
  // Sync watch data (plans, notes, emergency) to/from the Apple Watch companion.
  useWatchSync(
    watchCompanion.enabled && authorized,
    handleWatchVaultApproval,
    handleWatchNoteProcessed,
  );

  // Parses /emergency/:token (with optional ?pass_url=…) out of a universal
  // link or custom-scheme URL. Returns null if the URL is unrelated.
  const extractEmergencyParams = (url: string | null): { token: string; pass_url?: string } | null => {
    if (!url) return null;
    const match = url.match(/\/emergency\/([^/?#]+)/);
    if (!match) return null;
    const token = decodeURIComponent(match[1]);
    const passMatch = url.match(/[?&]pass_url=([^&]+)/);
    const pass_url = passMatch ? decodeURIComponent(passMatch[1]) : undefined;
    return { token, pass_url };
  };

  /**
   * Is the authed shell actually in the navigator yet?
   *
   * `authorized` flipping is not the same moment as `Main` existing. Signing in
   * swaps the root stack to the authed screens, but the app pushes `Main`
   * itself a beat later — the root state goes ["Accounts"] → ["Accounts","Main"]
   * about a second afterwards.
   *
   * A pending-nav drain that fires in that window issues navigate('Main', …) at
   * a navigator with no such route. The action is dropped silently (no thrown
   * error, nothing logged), `Main` then mounts at its own default tab, and the
   * user lands on Home instead of the screen their link named. That is ED-2633:
   * every emailed link — conversation invite, voice handoff, vault pointer —
   * opened while signed out died here.
   *
   * `navigationRef.isReady()` does NOT cover this: the container is mounted and
   * ready throughout; it's the route set that hasn't caught up.
   */
  const authedShellMounted = (): boolean => {
    try {
      const state: any = (navigationRef as any).getRootState?.();
      return Array.isArray(state?.routes) && state.routes.some((r: any) => r?.name === 'Main');
    } catch {
      return false;
    }
  };

  // Returns the URL if it's a short link domain URL that the app should handle in-app.
  // The InAppWebView follows the server redirect to the actual destination.
  const extractShortLinkUrl = (url: string | null): string | null => {
    if (!url) return null;
    const match = url.match(/^https?:\/\/(?:development\.)?eleplan\.link\/([A-Za-z0-9]{5,12})(?:[/?#]|$)/);
    return match ? url : null;
  };

  // Voice-handoff universal link: .../project/:id/voice[/:conversationId].
  // Scanned from the desktop QR; opens EllieVoice on this phone for the same
  // thread. conversationId is an optional 36-char uuid.
  const extractVoiceHandoffNav = (url: string | null): { projectId: number; conversationId?: string } | null => {
    if (!url) return null;
    const m = url.match(/\/project\/(\d+)\/voice(?:\/([0-9a-fA-F-]{36}))?(?:[/?#]|$)/);
    if (!m) return null;
    return { projectId: parseInt(m[1], 10), conversationId: m[2] || undefined };
  };

  // Returns all query params off any /register deep link, e.g.
  //   https://app.eleplan.com/register?code=ABCDEFG&source=partner
  //   com.eleplan.app://register?code=ABCDEFG
  // Returns null when the URL isn't a register link. Returns {} when it is a
  // register link but has no query string. Add new params (e.g. `source`,
  // `campaign`, `partner_id`) by destructuring them at the call site — no
  // changes to this helper required.
  const extractRegisterParams = (url: string | null): Record<string, string> | null => {
    if (!url) return null;
    if (!/(^|[/:])register(\b|[/?#])/i.test(url)) return null;
    const queryStart = url.indexOf('?');
    if (queryStart === -1) return {};
    const query = url.slice(queryStart + 1).split('#')[0];
    const params: Record<string, string> = {};
    for (const pair of query.split('&')) {
      if (!pair) continue;
      const eqIdx = pair.indexOf('=');
      const rawKey = eqIdx === -1 ? pair : pair.slice(0, eqIdx);
      const rawVal = eqIdx === -1 ? '' : pair.slice(eqIdx + 1);
      if (!rawKey) continue;
      try {
        const key = decodeURIComponent(rawKey).trim();
        const value = decodeURIComponent(rawVal).trim();
        if (key && value) params[key] = value;
      } catch {
        // Skip malformed pairs rather than fail the whole parse.
      }
    }
    return params;
  };

  /**
   * Returns a PendingNotificationNav for any /project/:id?...&prompt=1 universal link
   * (e.g. the "Ask for help" share URL produced by NativeSurveyModal). Mirrors the
   * push-notification path so both deep-link channels open the same question.
   *
   * Accepts any of:
   *   https://app.eleplan.com/project/123?p=PAGE&q=Q&s=Survey&prompt=1
   *   https://development.eleplan.org/project/123?p=PAGE&q=Q&s=Survey&prompt=1&assigned=1
   *   /project/123?...&prompt=1   (path-only — push notifications send this shape)
   */
  const extractSurveyPromptNav = (url: string | null): PendingNotificationNav | null => {
    if (!url) return null;
    const match = url.match(/\/project\/(\d+)\?([^#]*?\bprompt=1\b[^#]*)/);
    if (!match) return null;
    const projectId = Number(match[1]);
    if (!Number.isFinite(projectId) || projectId <= 0) return null;
    let params: URLSearchParams;
    try {
      params = new URLSearchParams(match[2]);
    } catch {
      return null;
    }
    return {
      projectId,
      screen: 'ProjectSurveys',
      openPageName:     params.get('p') || undefined,
      openSurveyName:   params.get('s') || undefined,
      openQuestionName: params.get('q') || undefined,
    };
  };

  // Sync the app icon badge with the unread inbox message count from Redux.
  // The badge is set to 0 whenever the app comes to the foreground (user sees the app).
  // It is incremented by InboxScreen via UPDATE_CHAT_BADGE when Ably delivers a new message.
  useEffect(() => {
    const unsubscribe = subscribeAIGlowOverlay(setAIGlowActive);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    Notifications.setBadgeCountAsync(unreadChats).catch(() => {});
  }, [unreadChats]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') Notifications.setBadgeCountAsync(0).catch(() => {});
    });
    return () => sub.remove();
  }, []);

  // Auto-register the Expo push token on every login / session restore, so a
  // permission granted in a prior session (e.g. during onboarding) reaches the
  // DB without a visit to Settings. It skips when the person switched push off
  // in the app.
  useEffect(() => {
    const accountId = account?.account_id;
    const cognitoId = user?.cognito_id;
    if (!authorized || !accountId || !cognitoId) return;
    autoRegisterPushToken(accountId, cognitoId).catch((err) => {
      // Non-critical, but log so a broken push pipeline is visible.
      debug.warn('Push token auto-register failed', err);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorized, account?.account_id, user?.cognito_id]);

  // Initialize WatchConnectivity only when watch companion gates are enabled.
  // Safe no-op when the native watch bridge is not linked yet.
  useEffect(() => {
    if (!authorized || !watchCompanion.enabled) return;
    activateWatchSession().catch(() => {});
  }, [authorized, watchCompanion.enabled]);

  // When watch companion is enabled, check Background App Refresh status once.
  // If it's off the user needs to enable it in Settings for vault QR notifications
  // to arrive when the app is backgrounded or killed.
  useEffect(() => {
    if (!authorized || !watchCompanion.enabled || Platform.OS !== 'ios') return;
    getBackgroundRefreshStatus().then((status) => {
      if (status === 'denied') {
        Alert.alert(
          'Enable Background App Refresh',
          'Background App Refresh is off for Eleplan. Without it, Watch Vault requests may not arrive when the app is closed.\n\nEnable it in Settings → Eleplan → Background App Refresh.',
          [
            { text: 'Not Now', style: 'cancel' },
            {
              text: 'Open Settings',
              onPress: () => Linking.openURL('app-settings:').catch(() => {}),
            },
          ],
        );
      }
    }).catch(() => {});
  }, [authorized, watchCompanion.enabled]);

  // Push a minimal session snapshot for watch-side context hydration.
  useEffect(() => {
    if (!authorized || !watchCompanion.enabled) return;
    if (!account?.account_id || !user?.cognito_id) return;
    pushWatchContext({
      type: 'session_snapshot',
      accountId: account.account_id,
      cognitoId: user.cognito_id,
      authorized: true,
      sentAt: new Date().toISOString(),
    }).catch(() => {});
  }, [authorized, watchCompanion.enabled, account?.account_id, user?.cognito_id]);

  // Notify the watch whenever the companion toggle changes so it can show a
  // disabled screen immediately instead of showing stale data.
  const prevWatchEnabledRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!authorized) return;
    const prev = prevWatchEnabledRef.current;
    prevWatchEnabledRef.current = watchCompanion.enabled;
    // Skip the initial mount — no prior state to diff against.
    if (prev === null) return;
    if (prev === watchCompanion.enabled) return;
    const msg = watchCompanion.enabled
      ? { type: 'companion_enabled' }
      : { type: 'companion_disabled' };
    pushWatchContext(msg).catch(() => {});
    sendWatchMessage(msg).catch(() => {});
  }, [authorized, watchCompanion.enabled]);

  // While the app stays open in the foreground, periodically sync health data.
  // Foreground-return sync already exists below; this covers long active sessions.
  useEffect(() => {
    if (!authorized || bootstrapping) return;

    const timer = setInterval(() => {
      if (appState.current === 'active') {
        runHealthSync().catch(() => {});
      }
    }, ACTIVE_HEALTH_SYNC_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [authorized, bootstrapping]);

  // Register the iOS BGAppRefreshTask for health sync so the OS can wake the
  // app and run a sync even when it's fully backgrounded or the screen is off.
  useEffect(() => {
    if (!authorized || bootstrapping) return;
    registerHealthBackgroundTask().catch(() => {});
    registerAppleCalendarTask().catch(() => {});
    registerAppleRemindersTask().catch(() => {});
    registerLocationBackgroundUpdates().catch(() => {});
    return () => {
      // Unregister on sign-out (authorized → false) to stop background wake-ups.
      unregisterHealthBackgroundTask().catch(() => {});
      unregisterAppleCalendarTask().catch(() => {});
      unregisterAppleRemindersTask().catch(() => {});
      unregisterLocationBackgroundUpdates().catch(() => {});
    };
  }, [authorized, bootstrapping]);

  // The Always grant lands when the person answers iOS's "Change to Always
  // Allow" dialog, which can be long after requestBackgroundPermissionsAsync
  // has resolved: expo resolves it on a 1.5 s timeout when the first dialog
  // already left the app inactive. Settings (and onboarding) register right
  // after that request, still see While Using, and skip, so the task only
  // started on the next cold launch. The app returns to the foreground from
  // every permission dialog, so registering again then picks the grant up;
  // registerLocationBackgroundUpdates is a no-op once the task is running.
  useEffect(() => {
    if (!authorized || bootstrapping) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') registerLocationBackgroundUpdates().catch(() => {});
    });
    return () => sub.remove();
  }, [authorized, bootstrapping]);

  useEffect(() => {
    if (initRanRef.current) return;
    initRanRef.current = true;

    // Seed system settings (maintenance mode, flags) BEFORE auth so the global
    // MaintenanceOverlay can gate even the unauthenticated login screen. Without
    // this, settings were only fetched inside restoreSession (authed path), so a
    // signed-out visitor could reach /login during a maintenance window.
    // /users/get-settings is public; fire-and-forget, and the Ably channel
    // (useMaintenanceMode) keeps it live afterwards.
    apiGet('/users/get-settings')
      .then((res) => {
        if (res?.payload) dispatch({ type: SET_SETTINGS, payload: res.payload });
      })
      .catch(() => {});

    // If the web app is served on a partner subdomain (<subdomain>.eleplan.com),
    // resolve that host to the partner's branding so the login + register
    // screens paint the partner's logo and colors before sign-in. No-op on
    // native and on the apex/app hosts. Fire-and-forget — branding is a
    // progressive enhancement and never blocks boot.
    dispatch(hydrateBrandingFromHost());

    const init = async () => {
      // Check for a device-login deep link BEFORE deciding whether to restore or bootstrap.
      // This eliminates the race where restoreSession() finishes first (finds no token),
      // shows the Login screen, and then loginWithDeviceTicket fires moments later causing a flash.
      const initialUrl = await Linking.getInitialURL();
      // An AI assistant's consent link that opened the app (cold start) belongs in the
      // browser the assistant opened; see handOffConnectLink. Nothing below matches it.
      handOffConnectLink(initialUrl);
      const extractTicket = (url: string | null) => {
        if (!url) return null;
        const ticketId = url.match(/[?&]ticket=([^&]+)/)?.[1]?.trim();
        const accountId = url.match(/[?&]account_id=([^&]+)/)?.[1]?.trim();
        const cognitoId = url.match(/[?&]cognito_id=([^&]+)/)?.[1]?.trim();
        if (ticketId && accountId && cognitoId) {
          return {
            ticketId: decodeURIComponent(ticketId),
            accountId: decodeURIComponent(accountId),
            cognitoId: decodeURIComponent(cognitoId),
          };
        }
        return null;
      };

      const ticket = extractTicket(initialUrl);
      const emergencyParams = extractEmergencyParams(initialUrl);
      if (emergencyParams) setPendingEmergencyCard(emergencyParams);
      const shortUrl = !emergencyParams ? extractShortLinkUrl(initialUrl) : null;
      if (shortUrl) setShortLinkDeepLink(shortUrl);
      // Cold-launch vault-item link — stash; the drain effect resolves + opens
      // once the user is authed and the navigator is mounted.
      const coldVaultCode = !emergencyParams && !shortUrl ? extractVaultLinkCode(initialUrl) : null;
      if (coldVaultCode) setPendingVaultCode(coldVaultCode);
      // Universal-link survey prompt (e.g. tapping the "Ask for help" share URL).
      // Stash the intent now so ProjectsScreen consumes it on mount, identical
      // to how cold-launch push notifications are handled below.
      const surveyPromptNav = !emergencyParams && !shortUrl
        ? extractSurveyPromptNav(initialUrl)
        : null;
      if (surveyPromptNav) setPendingNotificationNav(surveyPromptNav);

      // Cold-launch voice handoff (QR scanned while the app was closed). Stash;
      // the drain effect navigates once authed + the navigator is mounted.
      const voiceNav = !emergencyParams && !shortUrl && !surveyPromptNav
        ? extractVoiceHandoffNav(initialUrl)
        : null;
      if (voiceNav) setPendingVoiceHandoff(voiceNav);

      // Cold-launch project chat (conversation invite or desktop handoff).
      // Stash and drain once auth plus the navigation shell are ready.
      const projectChatNav = !emergencyParams && !shortUrl && !surveyPromptNav && !voiceNav
        ? extractProjectChatDeepLink(initialUrl)
        : null;
      if (projectChatNav) queueProjectChat(projectChatNav);

      // Cold-launch AI-assistant review link (/project/:id/suggestions[?note=<id>]).
      // Stash it like the survey prompt above: ProjectsScreen consumes it once the
      // user is signed in and Plans has mounted.
      const suggestionsNav = !emergencyParams && !shortUrl && !surveyPromptNav && !voiceNav && !projectChatNav
        ? extractProjectSuggestionsDeepLink(initialUrl)
        : null;
      if (suggestionsNav) setPendingNotificationNav({ ...suggestionsNav, screen: 'AgentSuggestions' });

      // Cold-launch upload link (/project/:id/files?openUpload=1), the one the AI
      // connector hands Claude and Perplexity. Stashed like the review link above;
      // Plans opens Files with the upload sheet.
      const filesNav = !emergencyParams && !shortUrl && !surveyPromptNav && !voiceNav && !projectChatNav && !suggestionsNav
        ? extractProjectFilesDeepLink(initialUrl)
        : null;
      if (filesNav) setPendingNotificationNav({ ...filesNav, screen: 'ProjectFiles' });

      // Cold-launch /register?... → stash any recognised prefill fields so
      // RegisterScreen can populate its inputs on mount, and steer the auth
      // stack at Register instead of Login so the user lands directly on the
      // form. Mirrors the web's URL-driven prefill (first_name, last_name,
      // email, code, ref). `ref` is the partner subdomain the co-branded
      // eleplan.com/<slug> landing appends; /register is a universal link, so
      // an installed app intercepts that tap and this is the ONLY place the
      // partner attribution can survive into RegisterScreen (there's no
      // window.location to fall back on). To support more fields, extend
      // RegisterPrefill in utilities/pendingRegisterPrefill.ts and pull the
      // key through here.
      const registerParams = extractRegisterParams(initialUrl);
      if (registerParams) {
        const { first_name, last_name, email, code, ref } = registerParams;
        await setPendingRegisterPrefill({ first_name, last_name, email, code, ref });
        // `code` is the pre-minted, email-specific partner invitation. Unlike
        // the one-shot form prefill it must survive Register -> Login, MFA,
        // OAuth redirects, and app restarts until an authenticated endpoint
        // accepts or rejects it. A bare `ref` landing slug deliberately never
        // enters this store; existing-account auto-linking is invite-only.
        if (code) await setPendingPartnerInvite({ code, email });
        setOnboardingTarget('register');
      }

      if (ticket) {
        // Cold launch via QR scan: use the ticket to log in, skip normal session restore
        await dispatch(loginWithDeviceTicket(ticket.ticketId, ticket.accountId, ticket.cognitoId));
      } else {
        // Normal launch: restore from stored tokens
        await dispatch(restoreSession());
      }

      // Show onboarding on first launch — but NOT to someone who arrived with an
      // explicit intent.
      //
      // `registerParams` means they tapped something that said "set up my
      // account". The block above has already stashed their prefill and set
      // onboardingTarget so the stack opens on Register — its own comment says
      // "so the user lands directly on the form". The carousel is what stopped
      // that happening: it returns INSTEAD of the Stack.Navigator (see below),
      // so Register was never mounted and the person got eight slides instead.
      //
      // That cost a real signup. The final slide offered "Sign In" as its
      // primary button, which overwrote the captured register intent, and a
      // lead with no account landed on a login screen. She wrote in saying the
      // link doesn't work and never appeared in `users` at all.
      //
      // A cold app-store launch still gets the carousel: there it is the only
      // place someone learns what Eleplan is. Someone arriving from our own
      // email has already decided, and asking them to sit through a product
      // tour — including two OS permission prompts — before they can type an
      // email address is how you lose them.
      //
      // Nor to someone an AI assistant sent here to approve a connection
      // (/connect/authorize?request=…): same mechanism, they would never reach
      // the sign-in or the consent page the link is for. Not recorded as seen,
      // though — that flag is for the tour itself.
      //
      // Nor to someone holding an invite's claim link (/claim?t=…): they were
      // invited, and the link opens the screen where they choose a passkey or
      // a password. Their first run is over too, like a signup link's.
      const claimLink = isClaimInviteUrl(initialUrl);
      if (!ticket && !registerParams && !isConnectAuthorizeUrl(initialUrl) && !claimLink) {
        const seenOnboarding = await hasSeenOnboarding();
        if (!seenOnboarding) {
          setShowOnboarding(true);
        }
      } else if (registerParams || claimLink) {
        // Record it, or the skip is per-launch rather than permanent.
        // ONBOARDING_DONE_KEY is written ONLY by the carousel itself and the
        // Settings replay, so skipping it leaves the flag unset: she registers
        // from the email link, uses the app, signs out weeks later, and the
        // next launch has no deep link — the gate above fires and she gets the
        // full first-run tour, whose primary button is "Sign In". That is the
        // original bug arriving late.
        //
        // "Seen" is the wrong word for what the flag means; "first run is over"
        // is the right one, and it is over for someone who arrived holding a
        // signup link.
        await markOnboardingComplete();
      }

      // Restore navigation personas from AsyncStorage into Redux
      try {
        const personasStr = await AsyncStorage.getItem('@eleplan/nav_personas');
        if (personasStr) {
          const personas = JSON.parse(personasStr);
          if (Array.isArray(personas)) {
            dispatch({ type: SET_NAV_PERSONAS, payload: personas });
          }
        }
      } catch {
        // Non-critical — defaults to empty array
      }

      hapticSplashFade();
      setBootstrapping(false);
      runHealthSync().catch(() => {});

      const initialSiriShortcut = await getInitialSiriShortcut();
      if (initialSiriShortcut) {
        debug.log('ui', 'Initial Siri shortcut found on launch', initialSiriShortcut);
        queueSiriShortcut(initialSiriShortcut);
      }

      // Cold-launch: if the app was killed and opened via a notification tap,
      // the addNotificationResponseReceivedListener fires before the useEffect
      // registers it.  We parse the intent here and store it in a module-level
      // singleton; ProjectsScreen consumes it on mount and handles the navigation
      // directly from within the fully-mounted PlansStack — avoiding the race
      // where lazy-loaded inner navigators aren't ready for a 3-level nested navigate.
      //
      // expo-notifications doesn't ship a web shim for getLastNotificationResponse
      // (it's an APNs/FCM cold-launch concept that doesn't map onto the browser
      // Push API). Calling it on web throws `UnavailabilityError` synchronously,
      // which red-screens the console without producing a useful behaviour. The
      // web equivalent — service-worker `notificationclick` → window.focus() — is
      // wired up separately in DesktopLayout via the `NOTIFICATION_CLICKED`
      // postMessage handler, so there's nothing to do here for web/Electron.
      const lastResponse = isWeb ? null : Notifications.getLastNotificationResponse();
      if (lastResponse) {
        const initialNotificationId = String(
          (lastResponse as any)?.notification?.request?.identifier
          || (lastResponse as any)?.notification?.request?.trigger?.requestIdentifier
          || '',
        );
        if (initialNotificationId && handledInitialNotificationRef.current === initialNotificationId) {
          return;
        }
        if (initialNotificationId) {
          handledInitialNotificationRef.current = initialNotificationId;
        }

        const responseData: Record<string, any> = (lastResponse.notification?.request?.content?.data as any) ?? {};
        const handledRemoteSync = await handleRemoteHealthSyncRequest(responseData);
        if (handledRemoteSync) {
          (Notifications as any)?.clearLastNotificationResponseAsync?.().catch(() => {});
          return;
        }
        // Unlike handleRemoteHealthSyncRequest above, a check-now push is also
        // user-actionable (the recipient's "I'm OK" button and the tap-to-open
        // confirm sheet, both handled by handleNotificationResponse below) —
        // so its own "handled" return (true whether freshly handled or already
        // deduped by request_id) must not skip the fall-through that follows.
        // Only run its data pull here; let the rest of this function decide
        // what the user's actual tap/action does.
        await handleWellnessCheckNowRequest(responseData);
        // "Your link was opened": the same stash-and-drain as a conversation
        // invite, which waits for sign-in and the authed shell.
        const guestLinkTarget = guestLinkOpenedTarget(responseData);
        if (guestLinkTarget) {
          trackNotificationOpen(lastResponse);
          queueProjectChat(guestLinkTarget);
          (Notifications as any)?.clearLastNotificationResponseAsync?.().catch(() => {});
          return;
        }
        const parsed = parseNotificationResponseForPending(lastResponse);
        if (parsed) {
          // The handoff waits for sign-in and Main; Plans consumes the intent.
          setPendingNotificationNav(parsed);
        } else {
          // Non-survey notification (external URL, etc.) — handle normally.
          let attempts = 0;
          const tryNav = () => {
            if (navigationRef.isReady()) {
              handleNotificationResponse(lastResponse);
            } else if (attempts++ < 15) {
              setTimeout(tryNav, 300);
            }
          };
          setTimeout(tryNav, 300);
        }
        (Notifications as any)?.clearLastNotificationResponseAsync?.().catch(() => {});
      }
    };

    init();
  }, [queueSiriShortcut, handleRemoteHealthSyncRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Route a deep link to whatever screen owns it.
  //
  // Hoisted out of the Linking listener below (where it used to live) so the
  // short-link WebView can hand in-app destinations to the SAME dispatch when it
  // recognises one — see shortLinkOpensNatively. One routing table, not two that
  // drift. Behaviour is otherwise unchanged: same body, same deps as the effect
  // that used to own it, so it is rebuilt (and the listener re-subscribed) on
  // exactly the same renders as before.
  const handleUrl = useCallback((url: string | null) => {
      if (!url) return;
      // An AI assistant's consent link that opened the app (already running) belongs in the
      // browser the assistant opened; see handOffConnectLink.
      if (handOffConnectLink(url)) return;
      // Emergency card deep link (webapp URL with /emergency/:token) — drives
      // the EmergencyCardScreen via the pending-nav effect below.
      const emergencyParams = extractEmergencyParams(url);
      if (emergencyParams) {
        setPendingEmergencyCard(emergencyParams);
        return;
      }
      // Vault-item pointer link (app universal link /vault-link/:code) — resolve
      // via the authenticated endpoint and open the credential's sheet in place.
      // Never opens a browser; the value is revealed only on-device behind biometric.
      const vaultCode = extractVaultLinkCode(url);
      if (vaultCode) {
        resolveAndOpenVaultLink(vaultCode);
        return;
      }
      // Short link domain (eleplan.link/:code) — open in-app; server redirect routes to destination
      const shortUrl = extractShortLinkUrl(url);
      if (shortUrl) {
        setShortLinkDeepLink(shortUrl);
        return;
      }
      // Device-login ticket
      const ticketIdRaw = url.match(/[?&]ticket=([^&]+)/)?.[1]?.trim();
      const accountIdRaw = url.match(/[?&]account_id=([^&]+)/)?.[1]?.trim();
      const cognitoIdRaw = url.match(/[?&]cognito_id=([^&]+)/)?.[1]?.trim();
      if (ticketIdRaw && accountIdRaw && cognitoIdRaw) {
        const ticketId = decodeURIComponent(ticketIdRaw);
        const accountId = decodeURIComponent(accountIdRaw);
        const cognitoId = decodeURIComponent(cognitoIdRaw);
        dispatch(loginWithDeviceTicket(ticketId, accountId, cognitoId));
        return;
      }

      // Survey question prompt (e.g. "Ask for help" share URL). Route through the
      // same pending-nav pipeline as push notifications so the question modal opens.
      const surveyPromptNav = extractSurveyPromptNav(url);
      if (surveyPromptNav && navigationRef.isReady()) {
        setPendingNotificationNav(surveyPromptNav);
        // Drive the user to Plans so ProjectsScreen mounts (or its subscription
        // fires if it's already mounted) and consumes the intent.
        try {
          (navigationRef as any).navigate('Main', { screen: 'Plans' });
        } catch {
          // Navigator not ready yet — pending-nav listener will fire when ProjectsScreen mounts.
        }
        return;
      }

      // Voice handoff (QR scanned while the app is already running). Stash and
      // let the drain effect navigate (it waits on auth + navigator readiness).
      const voiceNav = extractVoiceHandoffNav(url);
      if (voiceNav) {
        setPendingVoiceHandoff(voiceNav);
        return;
      }

      // Project chat from a conversation-invite email or browser-to-desktop
      // handoff. Same stash-and-drain path handles warm and cold delivery.
      const projectChatNav = extractProjectChatDeepLink(url);
      if (projectChatNav) {
        queueProjectChat(projectChatNav);
        return;
      }

      // AI-assistant review link (/project/:id/suggestions) → the "Needs you" list,
      // with the finding open when the link names one (?note=, a care nudge's "View").
      // Same pipeline as the survey prompt above: stash the intent, then drive the
      // user to Plans so ProjectsScreen consumes it.
      const suggestionsNav = extractProjectSuggestionsDeepLink(url);
      if (suggestionsNav && navigationRef.isReady()) {
        setPendingNotificationNav({ ...suggestionsNav, screen: 'AgentSuggestions' });
        try {
          (navigationRef as any).navigate('Main', { screen: 'Plans' });
        } catch {
          // Navigator not ready yet — pending-nav listener will fire when ProjectsScreen mounts.
        }
        return;
      }

      // Upload link (/project/:id/files?openUpload=1) → the plan's Files screen with its
      // upload sheet open; the link the AI connector gives Claude and Perplexity, which
      // can't pass a file over. Same pipeline as the review link above.
      const filesNav = extractProjectFilesDeepLink(url);
      if (filesNav && navigationRef.isReady()) {
        setPendingNotificationNav({ ...filesNav, screen: 'ProjectFiles' });
        try {
          (navigationRef as any).navigate('Main', { screen: 'Plans' });
        } catch {
          // Navigator not ready yet — pending-nav listener will fire when ProjectsScreen mounts.
        }
        return;
      }

      // Warm reopen of /register?... — persist any recognised prefill fields
      // and (for unauthenticated users) jump them straight to Register so the
      // prefill is visible without a manual "Sign up" tap. Prefill values
      // always flow through AsyncStorage (read by RegisterScreen on mount) so
      // there's a single source of truth regardless of launch path.
      const registerParams = extractRegisterParams(url);
      if (registerParams) {
        // Same reason the cold path skips the carousel: while showOnboarding is
        // true this component returns <OnboardingScreen/> INSTEAD of the
        // Stack.Navigator, so the navigate below has no navigator to act on and
        // the person stays on the carousel holding a link that did nothing.
        //
        // Reachable on native: install from the store, get the carousel,
        // background the app, then tap the link in our email — that is a warm
        // universal link, not a cold launch, so the gate in init() never runs.
        setShowOnboarding(false);
        // Seed the route as well as dismissing the carousel. Dismissing is what
        // MOUNTS the Stack.Navigator (it was returned instead of, not beside),
        // and initialRouteName applies on that first mount — so this lands on
        // Register regardless of when the navigate below fires.
        //
        // The navigate alone is not enough and this is not belt-and-braces:
        // navigationRef.isReady() is false at exactly this moment, because the
        // navigator it would report on has not mounted yet. setShowOnboarding
        // only SCHEDULES the re-render. The cold path has always set this
        // (:2107); the warm path never did, so its only route to the form was a
        // single un-retried navigate with no fallback.
        setOnboardingTarget('register');
        const { first_name, last_name, email, code, ref } = registerParams;
        const writes = [setPendingRegisterPrefill({ first_name, last_name, email, code, ref })];
        if (code) writes.push(setPendingPartnerInvite({ code, email }));
        // Wait for both stores before navigating or accepting. RegisterScreen
        // consumes its prefill on mount, so navigating first creates a real
        // warm-link race where the form occasionally opens blank.
        Promise.all(writes.map((write) => write.catch(() => undefined))).then(() => {
          // A warm link can arrive while the recipient is already signed in,
          // so no Login bootstrap will run. Accept through the same
          // authenticated, email-matched path immediately.
          if (code && authorized) return dispatch(acceptPendingPartnerInvite());
          if (!authorized && navigationRef.isReady()) {
            (navigationRef as any).navigate('Register');
          }
          return undefined;
        }).catch(() => {});
      }
  }, [dispatch, authorized]);

  /**
   * Does this URL name a screen the app can open itself?
   *
   * Used only by the short-link WebView, which follows the server 302 to the
   * link's destination. A destination we own should land on the native screen,
   * not render as the web build inside a modal with none of the app's auth.
   *
   * Deliberately narrow: ONLY destinations whose handling in handleUrl always
   * navigates. Two exclusions are load-bearing —
   *   • /register: its branch navigates only when unauthenticated, so
   *     intercepting it for a signed-in user would close the sheet and show
   *     nothing at all.
   *   • short links themselves: the destination of a short link is never
   *     another short link, and treating one as native would hand it straight
   *     back to the WebView it came from.
   * Everything unrecognised — presigned S3 document URLs, snapshot share pages,
   * wallet passes, third-party pages — keeps loading in the WebView, unchanged.
   */
  const shortLinkOpensNatively = (url: string): boolean => {
    // Origin gate first, and it is not belt-and-braces. The matchers below look
    // for path fragments — extractEmergencyParams only needs `/emergency/`
    // anywhere in the URL — and a short link's destination is very often a
    // presigned S3 URL whose object key we do not control. A document filed
    // under an `emergency/` prefix would otherwise be yanked out of the WebView
    // and into the EmergencyCard screen. Only our own web app can be a native
    // destination, so ask that first and the whole class of false positives on
    // third-party URLs goes away.
    if (!isOwnAppUrl(url)) return false;
    return (
      !!extractEmergencyParams(url)
      || !!extractVaultLinkCode(url)
      || !!extractVoiceHandoffNav(url)
      || !!extractSurveyPromptNav(url)
      // Project chat links (including conversation invites) are native once
      // the auth + authedShellMounted drain can navigate them.
      //
      // THIS LIST HAS TO AGREE WITH handleUrl. It is the second half of one
      // routing decision and nothing enforces the pairing: handleUrl now
      // recognises seven destinations, four of which are intercepted, and the
      // three that are not are deliberate (see the exclusions above). Adding a
      // branch to handleUrl without deciding about this list is how a
      // destination silently keeps opening as a login screen — which is what
      // happened to this one.
      || !!extractProjectChatDeepLink(url)
      // An AI assistant's review link: handleUrl stashes it and goes to Plans.
      || !!extractProjectSuggestionsDeepLink(url)
      // The upload link the connector gives Claude and Perplexity: same hand-off.
      || !!extractProjectFilesDeepLink(url)
    );
  };

  const interceptShortLinkDestination = (url: string): boolean => {
    if (!shortLinkOpensNatively(url)) return false;
    setShortLinkDeepLink(null); // dismiss the WebView; the navigator takes it from here
    handleUrl(url);
    return true;
  };

  // Handle deep links when the app is already running (foreground or background → foreground).
  // Does NOT call getInitialURL() here — that is handled in the init effect above to avoid races.
  useEffect(() => {
    const sub = Linking.addEventListener('url', ({ url }) => handleUrl(url));
    return () => sub.remove();
  }, [handleUrl]);

  // Electron does not feed custom protocols through React Native Linking.
  // Main/preload buffer cold launches until this listener subscribes, then the
  // URL enters the exact same parser and post-auth drain as mobile/web links.
  useEffect(() => {
    if (!isElectron) return;
    const bridge = getElectronBridge();
    if (!bridge?.onDeepLink) return;
    return bridge.onDeepLink(handleUrl);
  }, [handleUrl]);

  // Drain a cold-launch vault-item link once the user is authed. resolveAndOpen
  // navigates into the Plans stack and opens the credential's sheet.
  useEffect(() => {
    if (!authorized || !pendingVaultCode) return;
    // resolveAndOpenVaultLink navigates into Plans, so it needs `Main` present
    // for the same reason the voice drain does.
    if (!navigationRef.isReady() || !authedShellMounted()) {
      if (vaultDrainTriesRef.current++ >= DRAIN_MAX_TRIES) {
        debug.warn('vault-link drain gave up waiting for the authed shell');
        // Reset here too. These refs outlive sign-out (only `authorized`
        // toggles; RootNavigator never unmounts), so a counter left at 51 made
        // the NEXT link fail on its first attempt with none of the intended
        // grace period.
        vaultDrainTriesRef.current = 0;
        setPendingVaultCode(null);
        return;
      }
      const timer = setTimeout(() => setVaultDrainNonce((n) => n + 1), 200);
      return () => clearTimeout(timer);
    }
    vaultDrainTriesRef.current = 0;
    const code = pendingVaultCode;
    setPendingVaultCode(null);
    resolveAndOpenVaultLink(code);
  }, [authorized, pendingVaultCode, vaultDrainNonce]);

  // Notification listeners — foreground receive + tap response
  useEffect(() => {
    const receiveSub = Notifications.addNotificationReceivedListener((notification) => {
      const data = (notification.request?.content?.data as any) ?? {};
      handleRemoteHealthSyncRequest(data).catch(() => {});
      handleWellnessCheckNowRequest(data).catch(() => {});
      handleCaregiverMessagePush(data).catch(() => {});
    });
    const responseSub = Notifications.addNotificationResponseReceivedListener((response) => {
      const data = (response.notification?.request?.content?.data as any) ?? {};
      // "Your link was opened" opens the shared conversation (see the cold path).
      const guestLinkTarget = guestLinkOpenedTarget(data);
      if (guestLinkTarget) {
        trackNotificationOpen(response);
        queueProjectChat(guestLinkTarget);
        return;
      }
      // Try silent handlers in order. Each returns true if it consumed the
      // notification, otherwise we fall through to the navigational handler.
      // handleWellnessCheckNowRequest is the one exception: a check-now push
      // is ALSO user-actionable (the "I'm OK" action / tap-to-open confirm
      // sheet, both inside handleNotificationResponse), so its own "handled"
      // result — true whether freshly handled or already deduped by
      // request_id — must not skip that fall-through the way a purely silent
      // data-pull handler's does. Run its data pull, then keep going.
      handleRemoteHealthSyncRequest(data).then((handledByHealth) => {
        if (handledByHealth) return;
        return handleWellnessCheckNowRequest(data).then(() => handleCaregiverMessagePush(data)).then((handledByCgm) => {
          if (!handledByCgm) handleNotificationResponse(response);
        });
      }).catch(() => {
        handleNotificationResponse(response);
      });
    });
    return () => {
      receiveSub.remove();
      responseSub.remove();
    };
  }, [handleRemoteHealthSyncRequest, handleWellnessCheckNowRequest, handleCaregiverMessagePush, queueProjectChat]);

  // Siri Shortcuts — listen for activations and navigate accordingly.
  // Hard-gated to iOS because react-native-siri-shortcut is iOS-only — the
  // listener helpers themselves return null on Android/web/Electron, so
  // running the effect there just produced a useless "could not be
  // registered" warning twice in development (StrictMode double-invokes
  // effects). Skipping the effect entirely keeps the dev console clean.
  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    const sub = addSiriShortcutListener((event: SiriShortcutEventPayload) => {
      if (!event?.activityType) return;
      if (!authorized || bootstrapping || !navigationRef.isReady()) {
        queueSiriShortcut(event);
        return;
      }
      handleSiriShortcut(event);
    });
    if (!sub) {
      // We're on iOS and the native module still didn't bind — that's a
      // real problem (likely missing pod install / native rebuild after
      // installing react-native-siri-shortcut), so keep the warning.
      debug.warn('Siri shortcut listener could not be registered');
      return;
    }
    return () => sub.remove();
  }, [authorized, bootstrapping, queueSiriShortcut]);

  // Flush any pending Siri shortcut once auth/bootstrap/navigation are ready.
  useEffect(() => {
    const nextShortcut = pendingSiriShortcutRef.current;
    if (!nextShortcut) return;
    if (!authorized || bootstrapping) return;
    if (!navigationRef.isReady()) {
      const timer = setTimeout(() => setPendingSiriNonce((n) => n + 1), 250);
      return () => clearTimeout(timer);
    }
    debug.log('ui', 'Flushing queued Siri shortcut', nextShortcut);
    pendingSiriShortcutRef.current = null;
    handleSiriShortcut(nextShortcut);
  }, [authorized, bootstrapping, pendingSiriNonce]);

  // Drain a pending emergency-card deep link once the Stack.Navigator is
  // mounted and not blocked by the lock screen. Auth-agnostic — the
  // EmergencyCard screen is registered in BOTH the auth and unauth stacks.
  useEffect(() => {
    if (!pendingEmergencyCard) return;
    if (bootstrapping || locked?.show) return;
    if (!navigationRef.isReady()) {
      const timer = setTimeout(() => setPendingEmergencyCard((p) => (p ? { ...p } : p)), 200);
      return () => clearTimeout(timer);
    }
    try {
      (navigationRef as any).navigate('EmergencyCard', pendingEmergencyCard);
    } catch (err) {
      debug.warn('emergency-card navigate failed', err);
    }
    setPendingEmergencyCard(null);
  }, [pendingEmergencyCard, bootstrapping, locked?.show]);

  // Drain a pending voice-handoff deep link once the user is authed and the
  // navigator is mounted. Unlike the emergency card, EllieVoice lives in the
  // authed Plans stack, so we wait for `authorized` (covers session-restore on
  // cold launch and the post-login case) before navigating.
  useEffect(() => {
    if (!pendingVoiceHandoff) return;
    if (bootstrapping || locked?.show) return;
    if (!authorized) return; // hold until session restores / user logs in
    // …and until `Main` is actually in the navigator — see authedShellMounted.
    if (!navigationRef.isReady() || !authedShellMounted()) {
      if (voiceDrainTriesRef.current++ >= DRAIN_MAX_TRIES) {
        debug.warn('voice-handoff drain gave up waiting for the authed shell');
        voiceDrainTriesRef.current = 0; // see the vault drain — the ref outlives sign-out
        setPendingVoiceHandoff(null);
        return;
      }
      const timer = setTimeout(() => setPendingVoiceHandoff((p) => (p ? { ...p } : p)), 200);
      return () => clearTimeout(timer);
    }
    voiceDrainTriesRef.current = 0;
    try {
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen: 'EllieVoice',
          params: { projectId: pendingVoiceHandoff.projectId, conversationId: pendingVoiceHandoff.conversationId },
        },
      });
    } catch (err) {
      debug.warn('voice-handoff navigate failed', err);
    }
    setPendingVoiceHandoff(null);
  }, [pendingVoiceHandoff, bootstrapping, locked?.show, authorized]);

  // Drain a pending project-chat deep link. A saved conversation goes straight
  // to Conversation; a handoff before the first saved turn opens ProjectChat.
  useEffect(() => {
    if (!pendingProjectChat) return;
    if (bootstrapping || locked?.show) return;
    if (!authorized) return; // hold until session restores / user logs in
    const targetKey = `${pendingProjectChat.accountId || ''}:${pendingProjectChat.projectId}:${pendingProjectChat.conversationId || ''}`;

    // Browser and desktop persist active account state independently. Prove
    // membership plus the exact project/account pairing, and switch/hydrate
    // before navigation so downstream chat calls cannot use stale scope.
    if (
      pendingProjectChat.accountId
      && verifiedDesktopChatRef.current !== targetKey
    ) {
      if (desktopAccountActivationRef.current) return;
      desktopAccountActivationRef.current = true;
      Promise.resolve(dispatch(activateDesktopHandoffAccount(
        pendingProjectChat.accountId,
        pendingProjectChat.projectId,
        pendingProjectChat.conversationId,
        { shouldCommit: () => latestProjectChatKeyRef.current === targetKey },
      ) as any)).then((result: any) => {
        if (latestProjectChatKeyRef.current !== targetKey) return;
        if (result?.cancelled) {
          latestProjectChatKeyRef.current = null;
          setPendingProjectChat(null);
          return;
        }
        if (!result?.success) {
          latestProjectChatKeyRef.current = null;
          setPendingProjectChat(null);
          dispatch(showNotification(
            'error',
            'Could not open desktop chat',
            result?.message || 'This plan is not available to your desktop login.',
          ));
          return;
        }
        if (result.partnerNavigationPending) {
          latestProjectChatKeyRef.current = null;
          setPendingProjectChat(null);
          return;
        }
        verifiedDesktopChatRef.current = targetKey;
      }).catch(() => {
        if (latestProjectChatKeyRef.current !== targetKey) return;
        latestProjectChatKeyRef.current = null;
        setPendingProjectChat(null);
        dispatch(showNotification(
          'error',
          'Could not open desktop chat',
          'Please try again from Eleplan web.',
        ));
      }).finally(() => {
        desktopAccountActivationRef.current = false;
        // Re-run the drain for the verified target, or for a newer link that
        // arrived while this account activation was in flight.
        setPendingProjectChat((current) => (current ? { ...current } : current));
      });
      return;
    }
    // …and until `Main` is actually in the navigator. isReady() is true from
    // the first render, so on the sign-out → tap link → sign-in path this
    // fired while the root was still ["Accounts"] and the navigate was
    // dropped in silence. Same failure and same fix as the voice drain above.
    if (!navigationRef.isReady() || !authedShellMounted()) {
      if (projectChatDrainTriesRef.current++ >= DRAIN_MAX_TRIES) {
        debug.warn('project-chat deep-link drain gave up waiting for the authed shell');
        projectChatDrainTriesRef.current = 0;
        setPendingProjectChat(null);
        return;
      }
      const timer = setTimeout(() => setPendingProjectChat((p) => (p ? { ...p } : p)), 200);
      return () => clearTimeout(timer);
    }
    projectChatDrainTriesRef.current = 0;
    try {
      const { screen, params } = projectChatNavigationTarget(pendingProjectChat);
      (navigationRef as any).navigate('Main', {
        screen: 'Plans',
        params: {
          screen,
          params,
        },
      });
    } catch (err) {
      debug.warn('project-chat deep-link navigate failed', err);
    }
    setPendingProjectChat(null);
    latestProjectChatKeyRef.current = null;
  }, [pendingProjectChat, bootstrapping, locked?.show, authorized, dispatch]);

  // After sign-in: ask for notification permission, once per install.
  //
  // This ask used to live in the first-run carousel, which fired it BEFORE the
  // person had an account. iOS allows one push prompt per install, so a refusal
  // from a stranger burned the channel permanently for someone who never became
  // a user. It is asked here instead, where they have an account and the
  // reminders it powers are about to matter to them.
  //
  // Same shape as the deferred security prompts below: a key, an effect gated
  // on `authorized`, and a modal that waits its turn.
  //
  // The OS state is the real gate, not the key.
  //
  // Keyed on its own, this fires for EVERY signed-in user on their next launch,
  // not for the new signups it is written for — nobody has the key yet, so the
  // whole existing base would get a full-screen permission sheet once. Worse,
  // it would be a sheet that can do nothing: the only thing behind Continue is
  // `requestPermissionsAsync`, and once a user has answered, the OS never shows
  // that dialog again. Someone who already granted push would be asked to
  // enable push, press Continue, and watch nothing happen.
  //
  // So `undetermined` is the condition — an ask is only worth a screen while
  // there is still an ask to make. That also covers the upgrade path for free:
  // anyone who answered the old carousel's prompt has a determined status and
  // is never shown this, whether they granted or refused.
  useEffect(() => {
    if (!authorized) {
      setSecurityPromptsDecided(false);
      setShowNotificationsPrompt(false);
      setNotificationsSheetOpen(false);
      return;
    }
    // Wait for the biometric / PIN decision below. Both effects fire on the
    // same `authorized` flip; when this one won the race by a few milliseconds
    // the push sheet started presenting and was then hidden again to make room
    // for the biometric modal. A native Modal hidden while it is still
    // presenting leaves iOS with an orphaned, invisible presentation that
    // swallows every touch until the app is relaunched. Two customers and the
    // founder reproduced exactly that on iOS 26.6 and 26.7 (2026-09-23): the
    // push sheet flashed for a second, vanished, and the home screen was dead.
    if (!securityPromptsDecided) return;
    (async () => {
      const alreadyAsked = await AsyncStorage.getItem(NOTIFICATIONS_PROMPTED_KEY);
      if (alreadyAsked) return;
      // A failure here must not open the sheet. `getPermissionsAsync` rejecting
      // says nothing about whether an ask is possible, and guessing "yes" puts
      // a modal in front of the app on a path we cannot see.
      let canStillAsk = false;
      try {
        const { status } = await Notifications.getPermissionsAsync();
        canStillAsk = status === 'undetermined';
      } catch {
        canStillAsk = false;
      }
      if (!canStillAsk) return;
      setShowNotificationsPrompt(true);
    })();
  }, [authorized, securityPromptsDecided]);

  // Open the push sheet only once no other prompt is up, and then leave it up
  // until its own onDone. Presenting it while another Modal is still animating
  // away is the other half of the orphaned-presentation bug, so give the
  // previous one a beat to finish dismissing.
  useEffect(() => {
    if (!showNotificationsPrompt || notificationsSheetOpen) return;
    if (showBiometricSetup || showPinSetup || passkeyPrompt?.show) return;
    const t = setTimeout(() => setNotificationsSheetOpen(true), 400);
    return () => clearTimeout(t);
  }, [showNotificationsPrompt, notificationsSheetOpen, showBiometricSetup, showPinSetup, passkeyPrompt?.show]);

  // After login: handle both deferred onboarding security selections and the
  // normal first-time biometric auto-prompt. Biometric is shown first; PIN is
  // chained to appear after the biometric modal is dismissed.
  useEffect(() => {
    if (!authorized) return;
    const decideSecurityPrompts = async () => {
      const biometricDeferred = await AsyncStorage.getItem(BIOMETRIC_DEFERRED_KEY);
      const pinDeferred = await AsyncStorage.getItem(PIN_DEFERRED_KEY);

      if (biometricDeferred === 'true') {
        // User explicitly opted into biometric during onboarding — show if hardware supports it
        await AsyncStorage.removeItem(BIOMETRIC_DEFERRED_KEY);
        const hasHardware = await LocalAuthentication.hasHardwareAsync();
        const isEnrolled = await LocalAuthentication.isEnrolledAsync();
        if (hasHardware && isEnrolled) {
          setShowBiometricSetup(true);
          // PIN will be chained in the BiometricSetupModal onDone callback below
          return;
        }
        // Hardware not available — fall through to PIN check below
      } else if (!pinDeferred) {
        // Neither deferred key set — user didn't come from the onboarding security step.
        // Use the normal auto-prompt for returning users / users who skipped onboarding.
        const should = await shouldPromptBiometricSetup();
        if (should) {
          setShowBiometricSetup(true);
          return;
        }
      }
      // If pinDeferred is set but biometricDeferred is not, the user explicitly chose
      // PIN only during onboarding — skip the biometric auto-prompt entirely.

      // Show PIN setup if deferred
      if (pinDeferred === 'true') {
        await AsyncStorage.removeItem(PIN_DEFERRED_KEY);
        if (!user?.pin) setShowPinSetup(true);
      }
    };
    // Every exit path, including the early returns and a thrown error, releases
    // the push-permission sheet. React batches this with the setShow* call
    // from the same tick, so the sheet never sees a frame where it may open
    // while the biometric modal is not yet up.
    decideSecurityPrompts().catch(() => {}).finally(() => setSecurityPromptsDecided(true));
  }, [authorized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stamp the time whenever the app is unlocked so the AppState handler can
  // ignore the `inactive → active` transition that iOS fires when the Face ID
  // dialog dismisses (the promise resolves before that event, causing an
  // immediate re-lock without this guard).
  useEffect(() => {
    if (locked?.show === false) {
      justUnlockedAt.current = Date.now();
    }
  }, [locked?.show]);

  // Lock the app when it returns from background (passkeys, biometric, or PIN set). Skip lock while OAuth is in progress.
  useEffect(() => {
    const sub = AppState.addEventListener('change', async (nextState) => {
      // Track whether the app reached a true background state.
      // Native OS overlays (contacts picker, photo picker, camera, etc.) only
      // transition active → inactive → active and never reach 'background'.
      // We must not lock in that case — only lock after a real background visit.
      if (nextState === 'background') {
        wentToBackground.current = true;
        backgroundedAt.current = Date.now();
      }

      const isForeground = nextState === 'active';
      const recentlyUnlocked = Date.now() - justUnlockedAt.current < 2000;
      // How long we were actually away. On the very first 'active' event of a
      // session there's no prior background timestamp, so treat that as zero
      // elapsed (no lock) — locking on cold-launch is the bootstrap path's
      // job, not the AppState handler's.
      const awayMs = backgroundedAt.current ? Date.now() - backgroundedAt.current : 0;

      if (wentToBackground.current && isForeground && authorized && !locked?.show && !oauthInProgress && !recentlyUnlocked) {
        wentToBackground.current = false;
        // Lock only when the user has truly been away for the configured
        // idle window (BG_RELOCK_GRACE_MS). Sub-window app-switches — copy a
        // code from Messages, pick a doc to upload, glance at a text — leave
        // the session unlocked, matching what the LockScreen subtitle
        // promises and what banking / 1Password-class apps do on top of the
        // OS device lock.
        if (awayMs >= BG_RELOCK_GRACE_MS) {
          const passkeys: any[] = Array.isArray(user?.passkeys) ? user.passkeys : [];
          const hasBiometric = await isBiometricEnabled();
          const hasPin = !!user?.pin;
          if (passkeys.length > 0 || hasBiometric || hasPin) {
            dispatch({ type: SET_LOCKED, payload: { show: true, inactive: true } });
          }
        }
        // Background health sync on foreground return (throttled to 15 min per project)
        runHealthSync().catch(() => {});
      }

      // Proactively refresh tokens whenever the app returns to foreground and the
      // user is authorized. This keeps the session alive without waiting for the
      // next API call, mirroring the web's automatic session refresh behavior.
      if (isForeground && authorized) {
        getFreshToken().catch(() => {});
      }

      if (isForeground) {
        wentToBackground.current = false;
        backgroundedAt.current = 0;
      }

      appState.current = nextState;
    });
    return () => sub.remove();
  }, [authorized, locked, oauthInProgress, user]);

  if (bootstrapping) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.background }}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }

  // Show onboarding before the auth navigator for first-time users
  if (showOnboarding && !authorized) {
    return (
      <OnboardingScreen
        onDone={async (action) => {
          setOnboardingTarget(action);
          setShowOnboarding(false);
        }}
      />
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <Stack.Navigator
        screenLayout={renderRootScreenSuspense}
        screenOptions={{
          headerShown: false,
          // Use iOS-style horizontal slide on all platforms.
          // Android's default is a vertical bottom-sheet card presentation which
          // causes the stuttering/jarring transition reported between Login and Register.
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          gestureEnabled: true,
          gestureDirection: 'horizontal',
          transitionSpec: {
            open: TransitionSpecs.TransitionIOSSpec,
            close: TransitionSpecs.TransitionIOSSpec,
          },
          // Web: defeat CardContent's page-overflow mode, which assumes
          // document.body scrolls — ours never does. See stackCardStyle.ts.
          ...APP_SHELL_CARD_STYLE,
        }}
        initialRouteName={!authorized && onboardingTarget === 'register' ? 'Register' : undefined}
      >
        {authorized ? (
          <>
            <Stack.Screen name="Accounts" component={AccountsScreen} options={{ headerShown: false }} />
            <Stack.Screen name="Main" component={AppShell} options={{ headerShown: false }} />
            <Stack.Screen
              name="OnboardingReplay"
              component={OnboardingReplayScreen}
              options={{ headerShown: false, presentation: 'modal', cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS }}
            />
            <Stack.Screen
              name="Ingestion"
              component={IngestionScreen as unknown as React.ComponentType<any>}
              options={{ headerShown: false, presentation: 'modal', cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS }}
            />
            <Stack.Screen
              name="InteractiveSurvey"
              component={InteractiveSurveyScreen}
              options={{ headerShown: false, presentation: 'modal', cardStyleInterpolator: CardStyleInterpolators.forVerticalIOS }}
            />
            <Stack.Screen name="EmergencyCard" component={EmergencyCardScreen as any} options={{ headerShown: false }} />
            <Stack.Screen name="ConnectAuthorize" component={ConnectAuthorizeScreen as any} options={{ headerShown: false, title: 'Connect an AI assistant' }} />
            <Stack.Screen name="Admin" component={AdminStack as any} options={{ headerShown: false }} />
          </>
        ) : (
          <>
            <Stack.Screen name="Login" component={LoginScreen as any} options={{ headerShown: false }} />
            <Stack.Screen name="MFA" component={MFAScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ForcePassword" component={ForcePasswordScreen as any} options={{ headerShown: false }} />
            <Stack.Screen name="Register" component={RegisterScreen} options={{ headerShown: false }} />
            <Stack.Screen name="ClaimInvite" component={ClaimInviteScreen as any} options={{ headerShown: false }} />
            <Stack.Screen name="PartnerRegister" component={PartnerRegisterScreen} options={{ headerShown: false }} />
            <Stack.Screen name="PartnerLanding" component={PartnerLandingScreen} options={{ headerShown: false }} />
            {/* Public — same screen registered here so unauth users can open the card */}
            <Stack.Screen name="EmergencyCard" component={EmergencyCardScreen as any} options={{ headerShown: false }} />
            {/* Same route as in the signed-in stack: signed out it renders the sign-in, and
                signing in keeps the route (and its `request` param) in place. */}
            <Stack.Screen name="ConnectAuthorize" component={ConnectAuthorizeScreen as any} options={{ headerShown: false, title: 'Connect an AI assistant' }} />
          </>
        )}
      </Stack.Navigator>

      {/* Global composer — mounted once at root so any screen can open the
          message composer via dispatch(openComposer(...)). Replaces the
          InboxScreen-only composer pattern; matches the "compose prominent,
          inbox useless" thesis. Gated on `authorized` so the unauthenticated
          tree doesn't pay for it. */}
      {authorized && <GlobalComposerHost />}

      {/* "Approve call from Ellie?" — presented when a voice_call_approval push
          is opened (call-in Ellie push-approve). Global so it can overlay from
          anywhere in the app. */}
      {authorized && <VoiceCallApprovalSheet />}

      {/* The message behind a campaign push. Global overlay, same pattern —
          tapping a campaign notification is usually a cold start, so the store
          holds the pending message until this mounts. */}
      {authorized && <CampaignMessageSheet />}

      {/* Full-screen "Are you OK?" confirm — presented when a wellness
          check-in push is tapped. Same global-overlay pattern. */}
      {authorized && <WellnessCheckInSheet />}

      {/* Lock screen overlays the entire app when active.
          Electron uses DesktopLockScreen (mounted in DesktopLayout) instead —
          it owns both the OS-driven lock signals (sleep, Cmd+L, idle from
          main.ts) AND the JS-driven `session.locked.show` so desktop users
          see one consistent unlock UI regardless of which path triggered it. */}
      {!isElectron && <LockScreen />}

      {/* A passkey account (passkey sign-up, or an invite accepted with a
          passkey) signed in with a device-generated password: hold the app
          until the passkey is saved or the person sets a password. */}
      {authorized && <FinishSignInGate />}

      {/* One-time passkey offer after registration (ED-2799); renders nothing unless session.passkeyPrompt is set. */}
      <PasskeySetupSheet />

      {/* Short link deep link (eleplan.link/:code) — follows server redirect
          in-app once the native shell has a stable modal presenter. */}
      <ShortLinkWebViewHost
        uri={shortLinkDeepLink}
        enabled={!locked?.show}
        onClose={() => setShortLinkDeepLink(null)}
        onInterceptUrl={interceptShortLinkDestination}
      />

      {/* Watch vault approval — shown when the watch requests a QR and VEK is not cached */}
      <WatchVaultApprovalSheet
        request={watchVaultApproval}
        onClose={() => setWatchVaultApproval(null)}
      />

      {/* Shown on the phone after a watch-sourced voice note finishes uploading
          + being AI-summarized — the watch doesn't have room to display this. */}
      <WatchNoteProcessedSheet
        request={watchProcessedNote}
        onClose={() => setWatchProcessedNote(null)}
      />

      {/* One-time biometric setup prompt shown after first login */}
      {/* Notification permission, post-signup. Held back while either security
          modal OR the passkey offer is up so they do not stack on a brand-new
          account — the flag is already set, so it simply appears once they
          close.

          The passkey offer matters most here: it is raised by confirmSignUp, so
          on a brand-new registration it and this sheet are triggered by the same
          event. Without this clause a new user's first screen after verifying
          their email is a permission sheet covering the passkey offer they never
          got to see. Ordering is deliberate — "sign in faster next time" is worth
          more at that moment than push, and it is the one with a closing action
          that brings this sheet up behind it. */}
      <Modal
        visible={notificationsSheetOpen}
        animationType="slide"
        statusBarTranslucent
      >
        <OnboardingScreen
          mode="permissions"
          onDone={async () => {
            await AsyncStorage.setItem(NOTIFICATIONS_PROMPTED_KEY, 'true');
            setNotificationsSheetOpen(false);
            setShowNotificationsPrompt(false);
          }}
        />
      </Modal>

      <BiometricSetupModal
        visible={showBiometricSetup}
        onDone={async () => {
          setShowBiometricSetup(false);
          // Chain PIN setup if the user opted in during onboarding
          const pinDeferred = await AsyncStorage.getItem(PIN_DEFERRED_KEY);
          if (pinDeferred === 'true') {
            await AsyncStorage.removeItem(PIN_DEFERRED_KEY);
            if (!user?.pin) setShowPinSetup(true);
          }
        }}
      />

      {/* PIN setup — shown after biometric or standalone if only PIN was selected */}
      <PinSetupModal
        visible={showPinSetup}
        onDone={() => setShowPinSetup(false)}
        onSkip={() => setShowPinSetup(false)}
      />

      {/* Global AI glow overlay (full-screen, above navigators) */}
      <AIGlowBorder active={aiGlowActive} />

      {/* Eleplan-admin passkey gate — blocks the app for admins who have no
          origin-scoped passkey, since every privileged admin action needs a
          fresh WebAuthn assertion. Sits below MaintenanceOverlay so a real
          maintenance window still trumps it. */}
      <AdminPasskeyGate />

      {/* Maintenance mode overlay — sits above everything including the lock screen */}
      <MaintenanceOverlay />
    </View>
  );
}
